import {
  type AxesSource,
  DEFAULT_GRAPH_SCALE,
  evaluate,
  type EntitySource,
  type Evaluated,
  parentsOf,
  regionFrame,
  regionIn,
  type SphereSection,
  variationTableAnchor,
  variationTableFor,
} from '../construction';
import {
  asymptotesOf,
  NotableKind,
  type HalfPlane,
  parseInequality,
  VariationProblem,
  windowBox,
  graphWindow,
  notablePointsOf,
  parseExpression,
} from '../expression';
import { type Vec2, vec } from '../math';
import {
  addEntity,
  asEntityId,
  createDocument,
  createEntity,
  DEFAULT_STYLE,
  isPlaneSource,
  toneForPlane,
  type EntityId,
  FIGURE_WEIGHT,
  FillStyle,
  LineStyle,
  type SceneDocument,
  solidBehind,
  solidBehindAll,
  type Style,
} from '../model';
import {
  baseExtent,
  coplanar,
  NO_ROTATION,
  offPlane,
  orientedBase,
  ProjectionKind,
} from '../solid';

import { lookupStatement, MARK_GROUPS, MEASURES, VARIADIC } from './grammar';

/**
 * The viewpoint a blackboard object belongs to, or `null` for a figure built on a solid.
 *
 * A short walk rather than a use of `solidBehind`: that one is named for solids and answers about
 * them, and teaching it a second word would make every caller wonder which it had got. Free planes
 * name their space outright, and everything else here is one step from one of those.
 */
function spaceBehindAll(document: SceneDocument, ids: readonly EntityId[]): EntityId | null {
  let agreed: EntityId | null = null;

  for (const id of ids) {
    const source = document.entities.get(id)?.source;
    const found =
      source?.op === 'space' ? id : source?.op === 'plane-free' ? source.space : undefined;
    if (found === undefined) continue;
    // Two viewpoints in one figure is not a figure. Refusing beats picking one.
    if (agreed !== null && agreed !== found) return null;
    agreed = found;
  }

  return agreed;
}

/**
 * Turning a written construction into a document.
 *
 * ## Errors are collected, not thrown
 *
 * A generated script is usually *mostly* right. Throwing on the first bad line gives back
 * nothing and tells the generator about one mistake; collecting them builds everything that
 * was valid and reports every line that was not. That is the difference between a tool a
 * language model can iterate against and one it cannot.
 *
 * A line that fails is skipped, and any later line naming its result fails in turn with its
 * own message — so the report says "line 4: unknown name `O`" rather than silently producing
 * a drawing with a hole in it.
 */

/**
 * How many marks a graph can carry before the figure stops saying anything.
 *
 * Twelve is two more than the busiest textbook figure that is still readable — a cubic with
 * three roots, two turning points and a y-intercept is six. Past this the labels overlap each
 * other and the curve, and the reader cannot tell which coordinate belongs to which dot.
 */
const MOST_MARKS = 12;

/**
 * Why a function has no variation table, in words the author can act on.
 *
 * Spelled out rather than passed through as a code, because the audience for a parse error is
 * whoever wrote the line — a teacher or a model — and "too-many-points" tells neither of them
 * that narrowing the range is the fix. The panel says the same four things in Vietnamese; these
 * are the script's, and the script's messages are English throughout.
 */
/**
 * Tick spacing for a region's axes: about four marks across the whole frame.
 *
 * Not shared with the graph window, because the two are asked a different question: a graph knows
 * what its curve does and picks a *height* as well as a width, while a region needs only the step.
 * Reaching into `graphWindow` for one number would mean sampling a function that does not exist
 * here.
 *
 * Four, and taken from the **whole span** rather than half of it, because a region is usually
 * small. `x ≥ 0, y ≥ 0, 2x + y ≤ 4` fits in three units, and a step aiming at five marks each way
 * put ticks every half unit — `-0.5 0.5 1 1.5 2 2.5` written along an axis a hundred world units
 * long, with the numbers touching. A short axis needs fewer marks, not marks of the same density.
 */
function niceRegionStep(span: number): number {
  const rough = span / 4;
  const magnitude = 10 ** Math.floor(Math.log10(Math.max(rough, 1e-6)));
  for (const multiple of [1, 2, 5]) {
    if (rough <= multiple * magnitude) return multiple * magnitude;
  }
  return 10 * magnitude;
}

const WHY_NO_TABLE: Readonly<Record<VariationProblem, string>> = {
  [VariationProblem.NoDerivative]:
    'it has no derivative (floor, ceil, round, sign, min, max have none).',
  [VariationProblem.Constant]: 'it is constant, so there is no variation to tabulate.',
  [VariationProblem.TooManyPoints]:
    'it has too many turning points in this range — narrow the interval on the `graph` line.',
  [VariationProblem.Empty]: 'it is defined nowhere in this range.',
};

export interface ScriptError {
  /** One-based, so it matches what an editor shows. */
  readonly line: number;
  readonly message: string;
  /** The offending line, for a report the reader can act on. */
  readonly text: string;
}

/**
 * The line weights a figure of a solid needs, named rather than numeric.
 *
 * A teacher writing a worksheet is choosing between *meanings* — this edge is hidden, this
 * plane is the one under discussion — not between hex codes and widths. Naming them also keeps
 * a page of figures consistent, which is most of what makes a set of diagrams look drawn by
 * one person.
 *
 * ## One weight for the whole figure
 *
 * Every preset draws at {@link FIGURE_WEIGHT}. A geometry figure is a *diagram*, not an
 * engineering drawing: it has no near and far to convey by line thickness, and every line in it
 * is a line of the same construction. Varying the weight only tells the reader that some lines
 * are more important than others, which is a claim the figure is in no position to make — the
 * important line is whichever one the question is about, and the question is not in the picture.
 *
 * It also failed on its own terms. Emphasis by weight put a 3.2-unit dash through the middle of
 * a cube, and a heavy dashed line does not read as *emphasised behind the block*; it reads as a
 * mistake, because the eye takes weight for nearness and dashes for distance and the two fight.
 *
 * ## Black and white, on purpose
 *
 * `accent` and `note` were red before that. A worksheet is printed on a school photocopier,
 * where red is a mid grey — so a figure whose emphasis was carried by colour arrives with the
 * emphasis *gone*, and worse, the accented line comes out lighter than the ordinary ones it was
 * meant to stand out from.
 *
 * What is left to distinguish with is tone, and only a little of it: `accent` is the darkest,
 * `note` slightly lighter than an ordinary line, `hidden` and `faint` lighter still so that
 * they recede. Grey prints as a thinner-looking dash, which is the right failure.
 *
 * ## Tone and dash are separate questions
 *
 * `solid`, `hidden` and `faint` answer *is this line covered by the solid* — a fact about the
 * figure, which the parser works out rather than asking. `accent` and `note` answer *how much
 * does this line matter* — a decision about the problem. So the last two deliberately leave
 * `lineStyle` alone, and an emphasised line running through a block stays dashed.
 */

export const STYLE_PRESETS: Readonly<Record<string, Partial<Style>>> = {
  solid: { stroke: '#1f2937', strokeWidth: FIGURE_WEIGHT, lineStyle: LineStyle.Solid },
  hidden: { stroke: '#6b7280', strokeWidth: FIGURE_WEIGHT, lineStyle: LineStyle.Dashed },
  faint: { stroke: '#9ca3af', strokeWidth: FIGURE_WEIGHT, lineStyle: LineStyle.Dashed },
  accent: { stroke: '#000000', strokeWidth: FIGURE_WEIGHT },
  note: { stroke: '#374151', strokeWidth: FIGURE_WEIGHT },
  /*
   * A plane, which is the one thing in a solid figure that is a *surface* rather than a line.
   *
   * Drawn as a wash rather than an outline because that is the whole difficulty: a cutting plane
   * given as three more segments is indistinguishable from three more edges of the solid, and
   * the reader has to be told in words which triangle is the plane. A tone says it at a glance,
   * and it says it in the only vocabulary a photocopied worksheet has.
   *
   * Solid rather than hatched, though hatching is the drawing-office convention for a section
   * and the canvas can do it. The SVG and PDF writers treat every fill as solid, so a hatched
   * plane would look one way on screen and another on paper — and paper is where a worksheet is
   * read. One appearance everywhere is worth more here than the right convention in one place.
   *
   * Light enough to keep the edges behind it legible: this is a plane *through* a figure, and a
   * wash that hides what it cuts has taken more away than it gave.
   *
   * ## How light, and what it is traded against
   *
   * Lighter than it was, on the report that it read as heavy beside the rest of the drawing.
   * The tone is now about eleven per cent below white rather than twenty; against the figure's
   * own lines — which are near-black — that is still an unmistakable change of surface, and the
   * hidden edges crossing behind it stay readable, which is the thing that matters most.
   *
   * The cost is at the other end, and it is worth stating rather than discovering: a wash this
   * light is near the limit of what a **photocopier** keeps. Copiers threshold rather than
   * shade, and somewhere below about ten per cent a tint stops being a tint and becomes paper.
   * A worksheet printed straight from the PDF is unaffected; one photocopied twice may lose it.
   * If that turns out to matter more than the screen does, this number is the whole fix.
   */
  plane: {
    stroke: '#374151',
    strokeWidth: FIGURE_WEIGHT,
    fill: '#eef1f6',
    fillStyle: FillStyle.Solid,
  },
};

export interface ScriptResult {
  readonly document: SceneDocument;
  readonly errors: readonly ScriptError[];
  /** How many statements produced an object. */
  readonly created: number;
  /**
   * The names the script gave its objects.
   *
   * Returned because the parser is the only thing that knows. A caller that wants to say
   * something about `M` — a test, a checker, a panel showing the object list — otherwise has to
   * reconstruct the mapping by re-parsing and counting ids in order, which is guesswork that
   * looks right until a statement stops consuming an id.
   */
  readonly names: ReadonlyMap<string, EntityId>;
}

export interface ParseOptions {
  readonly style?: Style;
}

export function parseScript(source: string, options: ParseOptions = {}): ScriptResult {
  /*
   * The document's own default, which is {@link FIGURE_WEIGHT} — one weight for every line,
   * whether it was drawn with the toolbar or written here. A caller supplying its own style is
   * taken at its word.
   */
  const style = options.style ?? DEFAULT_STYLE;
  const errors: ScriptError[] = [];
  const names = new Map<string, EntityId>();
  /**
   * What an object turned out to be, so a statement can be checked against its own result.
   *
   * Worked out on demand and remembered, rather than recorded as each entity is written. Several
   * statements — the solids above all — create a handful of entities at once through their own
   * paths, and a line that had to be added at every one of those would be forgotten at the next
   * one added. Asking the document is the version that cannot fall out of step.
   *
   * A single pass with no cycle guard is safe because the language has no forward references: a
   * source may only name what is already written.
   */
  const values = new Map<EntityId, Evaluated | null>();
  const valueOf = (of: EntityId): Evaluated | null => {
    const remembered = values.get(of);
    if (remembered !== undefined) return remembered;

    const entity = document.entities.get(of);
    if (entity === undefined) return null;

    const built = evaluate(
      entity.source,
      parentsOf(entity.source).map((parent) => valueOf(parent))
    );
    values.set(of, built);
    return built;
  };

  let document = createDocument();
  let counter = 0;
  let created = 0;

  /*
   * The solids this script has made, so `rim` and `foot` can point at one.
   *
   * The entity id and which base circle each named centre owns, and nothing else. The corners,
   * the hidden edges and the positions in space used to be remembered here and written into the
   * drawing; they are derived from the figure entity now, which is what lets the figure survive
   * being moved or resized.
   */
  const solids: { readonly id: EntityId; readonly rings: ReadonlyMap<string, number> }[] = [];

  /*
   * Every segment drawn so far, keyed by its two endpoints in a fixed order, so that the same
   * line written the other way round can be recognised. Holds the *name* it was given, because
   * that is what the message has to say for the repair to be obvious.
   */
  const segments = new Map<string, string>();

  /*
   * Where each `epure` point is in space, by the base name the script gave it.
   *
   * The drawing holds two flat projections and has thrown the third coordinate away, which is
   * exactly the coordinate every construction of descriptive geometry is about: a trace is where
   * a coordinate reaches zero, and a true length is the hypotenuse over the difference of two of
   * them. Reading them back off the page would mean inverting a projection the parser had just
   * performed, so they are kept.
   *
   * Placed rather than live, like `rim` and `foot` — a trace does not follow a dragged
   * projection, and re-running the script recomputes it. The alternative is a source op per
   * construction, which is the kernel this is deliberately not yet.
   */
  const epures = new Map<string, { along: number; away: number; high: number }>();

  /**
   * Draws a point of the two-view diagram: both projections, the line tying them, the labels.
   *
   * One function for `epure` and for every construction that produces a point — a trace is a
   * point of the drawing like any other, and it has to be drawn the same way or the diagram
   * stops being uniform. Returns the correspondence line's id so the caller can bind a name to
   * it, or the message to fail with.
   */
  const placeEpure = (
    base: string,
    along: number,
    away: number,
    high: number
  ): { readonly tie: EntityId } | { readonly error: string } => {
    const first = `${base}1`;
    const second = `${base}2`;
    for (const taken of [first, second]) {
      if (names.has(taken)) {
        return {
          error:
            `\`${base}\` declares \`${taken}\` itself, and something else already has that ` +
            'name. Delete the earlier line rather than renaming the projection',
        };
      }
    }

    const made: EntityId[] = [];
    for (const [label, at] of [
      [first, vec.vec2(along, high)],
      [second, vec.vec2(along, -away)],
    ] as const) {
      counter += 1;
      const id = asEntityId(`s${String(counter)}`);
      /*
       * The name is the entity's own `label`, not a free-standing `text` beside it. Both draw
       * the same letters; only the first is *attached*, so it follows the point, keeps clear of
       * the lines meeting there, and answers `labelled` in the corpus. A caption that merely
       * sits at the right coordinates is the thing a hand-built diagram produced, and it drifts
       * the moment anything moves.
       */
      document = addEntity(document, {
        ...createEntity(id, { op: 'free-point', position: at }, style, { zIndex: counter }),
        label,
      });
      created += 1;
      names.set(label, id);
      made.push(id);
    }
    epures.set(base, { along, away, high });

    /*
     * The correspondence line, drawn faint because it is construction rather than figure — and
     * drawn at all because it is the whole content of the diagram. Two projections that do not
     * share it are two projections of two different points.
     */
    const [top, bottom] = made;
    if (top === undefined || bottom === undefined) {
      return { error: 'could not place both projections' };
    }
    counter += 1;
    const tie = asEntityId(`s${String(counter)}`);
    document = addEntity(
      document,
      createEntity(
        tie,
        { op: 'segment', a: top, b: bottom },
        { ...style, ...(STYLE_PRESETS['faint'] ?? {}) },
        { zIndex: counter }
      )
    );
    created += 1;
    return { tie };
  };

  /**
   * What to say about a name that is not there.
   *
   * A solid names its *corners*, so `AB` looks like an edge and is not an object — and "unknown
   * name `AB`" is a true statement that leaves a writer none the wiser. It was the commonest
   * remaining failure across twenty problems put through a model, twice in one run, always the
   * same shape: `text AB "a"` on an edge nobody had drawn.
   *
   * Only when both halves really are known points, so a plain typo is not misread as an edge.
   */
  const missing = (name: string): string => {
    const first = name.slice(0, 1);
    const second = name.slice(1);
    const known = name.length >= 2 && names.has(first) && names.has(second);

    return known
      ? `unknown name \`${name}\` — a solid names its corners, not its edges. ` +
          `Draw it first: \`${name} = segment ${first} ${second}\``
      : `unknown name \`${name}\``;
  };

  source.split('\n').forEach((rawLine, index) => {
    const line = index + 1;
    const text = rawLine.trim();

    // Blank lines and comments are not statements and are not errors.
    const withoutComment = stripComment(text).trim();
    if (withoutComment === '') return;

    const fail = (message: string): void => {
      errors.push({ line, message, text });
    };

    const assignment = splitAssignment(withoutComment);
    if (assignment === null) {
      fail('expected `name = statement` or a bare statement');
      return;
    }
    const { name, rest } = assignment;

    if (name !== null && names.has(name)) {
      fail(`the name \`${name}\` is already used`);
      return;
    }

    const tokens = tokenise(rest);
    const keyword = tokens[0];
    if (keyword?.kind !== 'word') {
      fail('a statement has to start with a keyword');
      return;
    }

    /*
     * `label X "text"` names an existing object rather than creating one. Handled here
     * because it is the only statement that *modifies* something, which makes it a poor fit
     * for a table of constructors.
     */
    /*
     * `style X hidden` restyles an existing object.
     *
     * A figure of a solid needs its hidden edges to recede and the plane under discussion to
     * come forward. Without this the language can place every line of a cone correctly and
     * still produce a picture nobody can read.
     *
     * The separation is *not* by line weight — every line in a figure is drawn at
     * {@link FIGURE_WEIGHT}, and the reasoning for that is with the presets above. It is by
     * dash and by tone, both of which survive a photocopier.
     *
     * Handled here beside `label` because both *change* an object rather than making one, and
     * the table above is for statements that build.
     */
    /*
     * `hide X` keeps an object out of the picture without removing it.
     *
     * A drawn figure needs points that are not meant to be seen: the two feet of a cone's
     * silhouette are there to hang the outline on, and an anchor for a label is there to give
     * the label somewhere to be. Drawn, they read as marked points of the construction — which
     * is a claim about the figure that is simply false.
     *
     * Hiding rather than deleting, because everything built on them still needs them: the
     * outline is a segment to a foot, and removing the foot takes the outline with it.
     */
    if (keyword.value === 'hide') {
      const target = tokens[1];
      const targetId = target === undefined ? undefined : names.get(String(target.value));
      const entity = targetId === undefined ? undefined : document.entities.get(targetId);

      if (entity === undefined) {
        fail('hide takes an object: `hide P`');
        return;
      }

      document = addEntity(document, { ...entity, visible: false });
      return;
    }

    if (keyword.value === 'style') {
      const target = tokens[1];
      const name = tokens[2];
      const preset = name === undefined ? undefined : STYLE_PRESETS[String(name.value)];

      if (target === undefined || preset === undefined) {
        fail(`style takes an object and one of ${Object.keys(STYLE_PRESETS).join(', ')}`);
        return;
      }

      const targetId = names.get(String(target.value));
      const entity = targetId === undefined ? undefined : document.entities.get(targetId);
      if (entity === undefined) {
        fail(missing(String(target.value)));
        return;
      }

      document = addEntity(document, { ...entity, style: { ...entity.style, ...preset } });
      return;
    }

    /*
     * `marks C extrema` — the points on a curve that the question is about.
     *
     * "Cho hàm số y = f(x) có đồ thị như hình vẽ. Tìm giá trị cực đại." is unanswerable from a
     * bare curve: the reader sees *that* there is a maximum and cannot read *what* it is. The
     * figure in the book carries a dot at the turning point with `(-1; 2)` beside it, and the
     * whole exercise hangs on that one annotation.
     *
     * It was reachable before as `plot -1 2` — the teacher doing the calculus by hand and
     * typing where the dot goes. That is fine until the formula changes, and it is the wrong
     * division of labour on the "đề bài → hình" path, where the author is a language model:
     * the model knows what the problem is asking for, and the derivative is what a computer
     * is for. A wrong extremum typed as a coordinate is also invisible — nothing checks it
     * against the curve it is sitting on.
     *
     * A statement rather than something every graph does, because every mark drawn is one the
     * reader has to look at. `y = sin x` over [−6.5; 6.5] has nine of them, and a figure
     * carrying nine coordinate labels has not said which one the question means.
     */
    if (keyword.value === 'marks') {
      const target = tokens[1];
      const groups = Object.keys(MARK_GROUPS).join(', ');

      if (target?.kind !== 'word') {
        fail(`marks takes a graph, then any of ${groups}: \`marks C extrema\``);
        return;
      }

      const targetId = names.get(target.value);
      if (targetId === undefined) {
        fail(missing(target.value));
        return;
      }

      const entity = document.entities.get(targetId);
      const graph = entity?.source;
      if (graph?.op !== 'function-graph') {
        fail(`\`marks\` needs a graph: \`C = graph "x^2-4*x+3" -1 5\` and then \`marks C\``);
        return;
      }

      const kinds: NotableKind[] = [];
      let guides = false;
      for (const token of tokens.slice(2)) {
        /*
         * `guides` sits in the same list as the kinds because it answers the same question — how
         * the marks are drawn — and a second clause for one word would be ceremony. It swaps the
         * coordinate beside the dot for a dashed line to each axis with the value written where
         * it lands, which is what a figure asking "tìm f(2) dựa vào đồ thị" needs: a coordinate
         * printed beside the dot answers the question the figure is supposed to pose.
         */
        if (String(token.value) === 'guides') {
          guides = true;
          continue;
        }

        const group = MARK_GROUPS[String(token.value)];
        if (group === undefined) {
          fail(`\`marks\` takes any of ${groups}, and \`guides\``);
          return;
        }
        kinds.push(...group);
      }

      let formula;
      try {
        formula = parseExpression(graph.expression);
      } catch {
        // The `graph` line already reported why its formula would not parse. Saying it twice
        // sends the author to a line that is not the one to fix.
        return;
      }

      const points = notablePointsOf(
        formula,
        graph.from,
        graph.to,
        ...(kinds.length > 0 ? [kinds] : [])
      );

      /*
       * Refused rather than trimmed.
       *
       * A cap that silently keeps the first eight would put a figure on a worksheet that is
       * missing marks nobody was told about — and the reader has no way to tell an unmarked
       * curve from an over-full one. `sin x` on a wide interval is the case: the answer is to
       * narrow the range or ask for one kind, and both are the author's to choose.
       */
      if (points.length > MOST_MARKS) {
        fail(
          `\`marks\` found ${String(points.length)} points here, too many to label. ` +
            `Narrow the range, or ask for one kind: ${groups}`
        );
        return;
      }

      /*
       * One derived object, which is the same one the panel's checkboxes edit.
       *
       * This used to place a free point per mark, with the coordinates baked into each label.
       * That is correct when the script runs and wrong from the first edit afterwards: retype
       * the formula in the panel and the dots stay at the old curve's turning points, still
       * carrying the old coordinates. The script and the toolbar are meant to be two doors to
       * one drawing, so they had better not produce two kinds of object either.
       *
       * The points are computed above all the same — not to place them, but so that a script
       * asking for more marks than a figure can carry is told so on the line that asked.
       */
      counter += 1;
      document = addEntity(
        document,
        createEntity(
          asEntityId(`s${String(counter)}`),
          {
            op: 'graph-marks',
            graph: targetId,
            kinds: kinds.length > 0 ? kinds : [...Object.values(NotableKind)],
            guides,
          },
          style
        )
      );
      created += 1;
      return;
    }

    /*
     * `variation C` — the bảng biến thiên of a curve already drawn.
     *
     * The table is what a Vietnamese lesson is actually built around: it is the same information
     * as the graph read in the opposite direction, and it is how a student *derives* the shape
     * before drawing it. A great many problems ask for it and nothing else — "cho bảng biến
     * thiên như hình vẽ, tìm số nghiệm của phương trình".
     *
     * It has existed for a while and only on the toolbar, which means the "đề bài → hình" path
     * could never produce one. That is the whole reason this line exists: a model handed the
     * grammar can only write what the grammar has, so the most-asked-for figure in the syllabus
     * was unreachable from a problem statement. A feature no generated figure can use is half a
     * feature.
     *
     * Attached to the graph by name rather than taking a formula of its own, so the two cannot
     * disagree: retyping the curve's formula moves the table with it, exactly as the button does.
     */
    /*
     * `t = tangent C 2` — the tangent to a curve at a given abscissa.
     *
     * "Viết phương trình tiếp tuyến của đồ thị (C) tại điểm có hoành độ x₀ = 2" is a question
     * every exam sets, and the figure it needs is the curve with one straight line laid against
     * it. There was no way to draw that: `tangent` meant the tangent to a *circle* from a point
     * outside it, which is a different construction found by intersecting circles, and a teacher
     * wanting the other one had to compute the gradient by hand and lay a `line` through two
     * points they had also computed.
     *
     * The same keyword, because it is the word both audiences will reach for and the two forms
     * are told apart by the argument **on the same line**: a number is an abscissa, a name is
     * the external point. That is not the ambiguity `plot` and `point` were split to avoid —
     * that one turned on an `axes` line somewhere else in the script, so a reader could not tell
     * what a statement meant by looking at it. This one is decided by what is written on it.
     */
    if (keyword.value === 'tangent' && tokens[2]?.kind === 'number') {
      const target = tokens[1];
      if (target?.kind !== 'word' || tokens.length !== 3) {
        fail('tangent takes a graph and an abscissa: `t = tangent C 2`');
        return;
      }

      const targetId = names.get(target.value);
      if (targetId === undefined) {
        fail(missing(target.value));
        return;
      }

      if (document.entities.get(targetId)?.source.op !== 'function-graph') {
        fail(
          '`tangent <name> <number>` is the tangent to a **graph**; for a circle, `tangent c A`'
        );
        return;
      }

      counter += 1;
      const id = asEntityId(name ?? `s${String(counter)}`);
      document = addEntity(
        document,
        createEntity(id, { op: 'graph-tangent', graph: targetId, at: tokens[2].value }, style)
      );
      created += 1;
      if (name !== null) names.set(name, id);
      return;
    }

    /*
     * `region "x >= 0" "y >= 0" "2x + y <= 4"` — the mien nghiem of a system.
     *
     * "Bieu dien mien nghiem cua he bat phuong trinh bac nhat hai an" is the whole of one lesson
     * in lop 10 and the answer is a picture. Before this the only way to draw one was to work out
     * the corners by hand, place them with `plot`, join them into a `polygon` and shade it — at
     * which point the figure is a *drawing* of an answer rather than the answer, and nothing
     * checks the corners against the inequalities they came from.
     *
     * ## Why the shading and the boundaries are separate objects
     *
     * Because the polygon's own outline would be wrong. A system whose region runs off the page
     * is clipped to the frame, and the clipped edges are an artefact of the paper — outlining
     * them would show a reader a closed shape where the truth is a region that goes on. So the
     * wash is drawn outline-less and each boundary is drawn across the whole frame, solid or
     * dashed according to whether the line itself is part of the solution.
     *
     * That dashed-versus-solid distinction is not decoration: it is the difference between `<`
     * and `<=`, and a figure that draws them alike has answered a different question.
     */
    if (keyword.value === 'region') {
      const written = tokens.slice(1);
      if (written.length === 0) {
        fail('region takes one or more inequalities: `region "x >= 0" "2x + y <= 4"`');
        return;
      }

      const planes: HalfPlane[] = [];
      for (const token of written) {
        const plane = token.kind === 'string' ? parseInequality(token.value) : null;
        if (plane === null) {
          fail(
            'each argument has to be a quoted linear inequality in `x` and `y`, ' +
              'like `"2x + 3y <= 6"` or `"y > x - 1"`'
          );
          return;
        }
        planes.push(plane);
      }

      /*
       * The frame comes from the region rather than the other way round, and an empty system is
       * refused. A blank page and a page whose region is off-screen look identical to a reader,
       * so "no point satisfies all of these" has to be said in words — and it is a fact about the
       * system that the author needs to hear.
       */
      const found = regionFrame(planes);
      if (found === null) {
        fail('no point satisfies all of these at once, so there is no region to draw');
        return;
      }

      /*
       * Snapped out to whole ticks, so the frame ends on a numbered mark rather than at
       * 3.47 — the edge of a figure is a place a reader measures from.
       */
      const step = niceRegionStep(Math.max(found.maxX - found.minX, found.maxY - found.minY));
      const frame = {
        minX: Math.floor(found.minX / step) * step,
        maxX: Math.ceil(found.maxX / step) * step,
        minY: Math.floor(found.minY / step) * step,
        maxY: Math.ceil(found.maxY / step) * step,
      };
      const drawn = regionIn(planes, frame);

      if (!hasAxes(document)) {
        counter += 1;
        document = addEntity(
          document,
          createEntity(
            asEntityId(`s${String(counter)}`),
            {
              op: 'axes',
              scale: DEFAULT_GRAPH_SCALE,
              extent: (frame.maxX - frame.minX) / 2,
              extentY: (frame.maxY - frame.minY) / 2,
              center: {
                x: (frame.minX + frame.maxX) / 2,
                y: (frame.minY + frame.maxY) / 2,
              },
              step,
              numbered: true,
            },
            { ...style, stroke: '#6b7280', strokeWidth: 1 },
            { zIndex: -1 }
          )
        );
        created += 1;
      }

      /** An invisible corner. Drawn, it would claim the system had marked that point. */
      const hidden = (at: { readonly x: number; readonly y: number }): EntityId => {
        counter += 1;
        const id = asEntityId(`s${String(counter)}`);
        document = addEntity(document, {
          ...createEntity(
            id,
            {
              op: 'free-point',
              position: { x: at.x * DEFAULT_GRAPH_SCALE, y: at.y * DEFAULT_GRAPH_SCALE },
            },
            style
          ),
          visible: false,
        });
        created += 1;
        return id;
      };

      const placed = drawn.vertices.map(hidden);

      if (placed.length >= 3) {
        counter += 1;
        document = addEntity(
          document,
          createEntity(
            asEntityId(`s${String(counter)}`),
            { op: 'polygon', vertices: placed },
            /*
             * Outlined in its own fill colour rather than in nothing.
             *
             * `stroke: 'transparent'` was the obvious way to say "no outline" and it is the one
             * thing that must not be written here: the PDF writer parses a colour as hex and
             * falls back to **black**, so the wash would be outline-less on screen and boxed in
             * on the worksheet. A stroke the same colour as the fill is invisible in every
             * back-end, which is the property that actually matters.
             */
            {
              ...style,
              ...(STYLE_PRESETS['plane'] ?? {}),
              stroke: STYLE_PRESETS['plane']?.fill ?? '#eef1f6',
            },
            { zIndex: -1 }
          )
        );
        created += 1;
      }

      for (const boundary of drawn.boundaries) {
        const a = hidden(boundary.from);
        const b = hidden(boundary.to);

        counter += 1;
        document = addEntity(
          document,
          createEntity(
            asEntityId(`s${String(counter)}`),
            { op: 'segment', a, b },
            {
              ...style,
              lineStyle: boundary.strict ? LineStyle.Dashed : LineStyle.Solid,
            }
          )
        );
        created += 1;
      }
      return;
    }

    /*
     * `K = hull A C B2 D2` — the convex solid on points that know where they are in space.
     *
     * "Tính thể tích khối tứ diện ACB'D'" is a whole family of exercises, and until now the
     * figure for it had to be four segments drawn between corners: no faces, so nothing hidden,
     * nothing to cut, nothing to shade. The corners already carry their positions in space, so
     * naming them is the whole statement — the faces follow, because for a convex body they do.
     *
     * Refused rather than approximated when the points do not describe a body. Four in a plane
     * are a quadrilateral, and drawing something anyway would put a solid on the page that the
     * problem never contained.
     */
    /*
     * `K = cut p S` — the piece of a body a plane leaves, named by a corner it keeps.
     *
     * "Mặt phẳng (MNP) chia khối chóp thành hai phần; tính thể tích phần chứa đỉnh S" is a
     * standard question and the figure for it could not be drawn at all: the section was there,
     * and both halves of the body were still drawn whole around it.
     *
     * A corner rather than a side, because a plane's normal has an arbitrary sign — it depends on
     * the order its three points were named — so "above" would mean nothing to the reader. The
     * problem says which piece by naming something in it, and so does this.
     */
    if (keyword.value === 'cut') {
      const [, planeToken, cornerToken] = tokens;
      if (tokens.length !== 3 || planeToken?.kind !== 'word' || cornerToken?.kind !== 'word') {
        fail('cut takes a plane and a corner it keeps: `K = cut p S`');
        return;
      }

      const planeId = names.get(planeToken.value);
      const keeps = names.get(cornerToken.value);
      if (planeId === undefined) {
        fail(missing(planeToken.value));
        return;
      }
      if (keeps === undefined) {
        fail(missing(cornerToken.value));
        return;
      }

      const solid = solidBehindAll(document, [planeId, keeps]);
      if (solid === null) {
        fail('`cut` needs a plane and a corner of the same solid');
        return;
      }

      counter += 1;
      const id = asEntityId(name ?? `s${String(counter)}`);
      const source: EntitySource = { op: 'solid-cut', solid, plane: planeId, keeps };

      if (evaluate(source, [solid, planeId, keeps].map(valueOf)) === null) {
        fail(
          'this plane leaves no piece containing that corner: the corner may be on the plane ' +
            'itself, which is in both pieces, or the section may miss the body'
        );
        return;
      }

      document = addEntity(document, createEntity(id, source, style, { zIndex: counter }));
      created += 1;
      if (name !== null) names.set(name, id);
      return;
    }

    if (keyword.value === 'hull') {
      const named = tokens.slice(1);
      if (named.length < 4) {
        fail('hull takes at least four points: `K = hull A C B2 D2`');
        return;
      }

      const corners: EntityId[] = [];
      for (const token of named) {
        const found = token.kind === 'word' ? names.get(token.value) : undefined;
        if (found === undefined) {
          fail(missing(String(token.value)));
          return;
        }
        corners.push(found);
      }

      counter += 1;
      const id = asEntityId(name ?? `s${String(counter)}`);
      const source: EntitySource = { op: 'polyhedron', corners };

      if (evaluate(source, corners.map(valueOf)) === null) {
        fail(
          'these points do not make a solid: they need positions in space — corners of a figure, ' +
            'or points built on them — and they must not all lie in one plane'
        );
        return;
      }

      document = addEntity(document, createEntity(id, source, style, { zIndex: counter }));
      created += 1;
      if (name !== null) names.set(name, id);
      return;
    }

    if (keyword.value === 'variation') {
      const target = tokens[1];
      if (target?.kind !== 'word') {
        fail('variation takes a graph: `C = graph "x^3-3*x" -2.5 2.5` and then `variation C`');
        return;
      }

      const targetId = names.get(target.value);
      if (targetId === undefined) {
        fail(missing(target.value));
        return;
      }

      const entity = document.entities.get(targetId);
      const graph = entity?.source;
      if (graph?.op !== 'function-graph') {
        fail('`variation` needs a graph, which is the function it tabulates');
        return;
      }

      const table = variationTableFor(graph.expression, { from: graph.from, to: graph.to });

      // `null` is an unreadable formula, which the `graph` line has already reported in its own
      // words. Two red messages under one mistake sends the author to the wrong line.
      if (table === null) return;

      /*
       * A refusal carries its reason, because every one of them is a fact about the *function*
       * that the author needs — and one of them is fixable from the script.
       *
       * `sin(x)` has infinitely many stationary points and no finite table; narrowing the range
       * gives it one. Producing the first twelve of infinitely many instead would be a table a
       * teacher puts on a worksheet without checking a machine's arithmetic.
       */
      if (!table.ok) {
        fail(`\`variation\` cannot tabulate this function: ${WHY_NO_TABLE[table.problem]}`);
        return;
      }

      counter += 1;
      document = addEntity(
        document,
        createEntity(
          asEntityId(`s${String(counter)}`),
          {
            op: 'variation-table',
            graph: targetId,
            at: variationTableAnchor(graph, axesSource(document)),
          },
          // Thinner than the figure: a table is a grid read through, and rules as heavy as the
          // curve make a page that is tiring to look at.
          { ...style, strokeWidth: 1 }
        )
      );
      created += 1;
      return;
    }

    /*
     * `prism A B C . A2 B2 C2 at 0 0 size 240 220 110` and its pyramid twin.
     *
     * The one statement family that builds *many* objects, which is why it is here rather than
     * in the table: eight named points and twelve styled segments come out of one line.
     *
     * ## Why it earns its keep
     *
     * Two thirds of the exercise book is a solid, and every one of those problems opens by
     * naming its corners — `Cho hình chóp S.ABCD`, `Gọi I là trung điểm của CC'`. Writing that
     * by hand meant working out eight projected coordinates, typing twelve segments and
     * deciding by eye which three to dash: forty lines to reach the point where the *problem*
     * starts. The four cases taken from the book each cost exactly that, and each got the
     * dashes right only because I checked them against a picture.
     *
     * The dot mirrors how the source states it — `ABCD.A'B'C'D'`, `S.ABCD` — so the statement
     * can be read straight off the page, and it makes a miscounted ring impossible to write
     * silently.
     */
    if (SOLID_KEYWORDS.has(keyword.value)) {
      const spec = buildSolid(keyword.value, tokens.slice(1));
      if (typeof spec === 'string') {
        fail(spec);
        return;
      }

      /*
       * A solid names every corner it mentions, so a name already taken means the writer
       * declared one of them separately — `S = point 0 0` above a `cone S O`, which is the
       * commonest thing a model does with these. "Already used" is true and tells them nothing;
       * renaming the corner is the wrong repair and produces a figure whose apex is called
       * something the problem never mentions.
       */
      for (const corner of spec.order) {
        // A vertex the script did not name cannot collide with anything.
        if (corner === null) continue;
        if (names.has(corner)) {
          fail(
            `\`${keyword.value}\` declares \`${corner}\` itself, and something else already has ` +
              `that name. Delete the earlier line rather than renaming the corner`
          );
          return;
        }
      }

      /*
       * Three kinds of entity, and the split is the whole design.
       *
       * The **centre** is an ordinary free point, and the only draggable thing here: moving it
       * moves the solid, and everything else follows because everything else is derived from
       * it. Hidden, because it is a handle rather than a point of the figure.
       *
       * The **figure** holds the description and draws the shape — its own edges, dashed where
       * the body covers them, decided afresh every time anything changes. One object in the
       * list rather than a dozen, which is also how a reader thinks of it.
       *
       * The **corners** are derived points. They can be named, labelled and built on, and they
       * cannot be dragged out of the solid they belong to: one corner of a cube moved on its own
       * is not a cube, and an editor that allowed it would let the figure stop meaning what it
       * says without saying so.
       */
      /*
       * `at I` hangs the solid off a point that is already in the figure rather than off a
       * hidden handle of its own. The origin is read from this parent at evaluation, so the
       * solid *follows* it — which is what "mặt cầu ngoại tiếp" needs, because that centre is
       * derived from the solid it encloses and moves whenever the solid does.
       *
       * The cost is that the figure can no longer be dragged by its own centre: the anchor is
       * a construction, and constructions are moved by moving what they are built from. That is
       * the same bargain every derived point in the language makes.
       */
      let centre: EntityId;
      if (spec.atName === undefined) {
        counter += 1;
        centre = asEntityId(`s${String(counter)}`);
        document = addEntity(document, {
          ...createEntity(centre, { op: 'free-point', position: spec.at }, style, {
            zIndex: counter,
          }),
          visible: false,
        });
        created += 1;
      } else {
        const anchor = names.get(spec.atName);
        if (anchor === undefined) {
          fail(missing(spec.atName));
          return;
        }
        centre = anchor;
      }

      counter += 1;
      const figure = asEntityId(`s${String(counter)}`);
      document = addEntity(
        document,
        createEntity(
          figure,
          {
            op: 'solid-figure',
            solid: spec.solid,
            center: centre,
            width: spec.width,
            height: spec.height,
            depth: spec.depth,
            corners: spec.corners,
            foot: spec.foot,
            ...(spec.sections === undefined ? {} : { sections: spec.sections }),
            ...(spec.baseAngleAt === undefined
              ? {}
              : {
                  baseAngleAt: spec.baseAngleAt,
                  baseAngle: spec.baseAngle,
                  ...(spec.baseFar === undefined ? {} : { baseFar: spec.baseFar }),
                  ...(spec.baseFarOffset === undefined
                    ? {}
                    : { baseFarOffset: spec.baseFarOffset }),
                }),
            ...(spec.baseCorners === undefined ? {} : { baseCorners: spec.baseCorners }),
          },
          { ...style, ...(STYLE_PRESETS['solid'] ?? {}) },
          { zIndex: counter }
        )
      );
      created += 1;
      solids.push({ id: figure, rings: spec.rings });

      spec.order.forEach((corner, index) => {
        // An unnamed vertex still holds its place in the numbering, and nothing is drawn at it.
        if (corner === null) return;
        counter += 1;
        const id = asEntityId(`s${String(counter)}`);
        document = addEntity(document, {
          ...createEntity(id, { op: 'solid-vertex', solid: figure, index }, style, {
            zIndex: counter,
          }),
          // Named corners are the entire reason this statement exists, so they arrive
          // labelled. `label A "A'"` still overrides it for a name the grammar cannot spell.
          label: corner,
        });
        created += 1;
        names.set(corner, id);
      });

      return;
    }

    /*
     * `ground 420` and `epure A 60 30 40` — the two-view drawing of descriptive geometry.
     *
     * ## What this is, and what it deliberately is not
     *
     * An *đồ thức*: the ground line, and a point given once in space appearing as its two
     * projections, one above the line and one below, joined by the correspondence line they must
     * share. That is the opening chapters of any hình họa course and it is what the language had
     * no answer to at all — asked for one, a model built it out of `point` and `segment` by hand,
     * which works only because the diagram is flat, and left the helper points it used as text
     * anchors visible on the page.
     *
     * It is **not** descriptive geometry. Traces of a line or a plane, true length by rotation or
     * by the right-triangle method, changing the plane of projection, the third view, visibility
     * between skew lines — none of that is here, and none of it is a clause away: each is a
     * construction *within* the two-view system, and the system needs its own kernel the way
     * solids needed `named.ts`. What is below is the coordinate frame those constructions would
     * be built on, not a substitute for them.
     *
     * ## Why sugar rather than a source op
     *
     * Because nothing here is a new *kind* of object: two points, a line between them, and a
     * layout rule. `foot` in a flat figure took the same decision for the same reason — a second
     * op meaning what an existing one already means is how two answers to one question start to
     * disagree.
     *
     * The cost is that the two projections are *placed*, not tied: drag `A1` and the
     * correspondence with `A2` is broken, exactly as dragging a `rim` point off its circle would
     * be. Re-running the script restores it.
     *
     * ## The convention
     *
     * `x` runs right along the ground line. The vertical projection A1 sits **above** it at the
     * point's height (độ cao, z), and the horizontal projection A2 **below** it at the point's
     * distance from the vertical plane (độ xa, y). So `epure A 60 30 40` is A(x = 60) drawn 40
     * up and 30 down — which is what "cách P1 30, cách P2 40" says.
     */
    if (keyword.value === 'ground') {
      const length = tokens[1];
      if (tokens.length !== 2 || length?.kind !== 'number' || length.value <= 0) {
        fail('ground takes the length of the projection axis: `ground 420`');
        return;
      }
      /*
       * The axis runs from a short lead-in on the left to the length given, rather than being
       * centred on the origin. Both are defensible and only one is predictable: a point is
       * placed by its distance *along* the axis, so `epure A 60 …` has to land 60 from where the
       * reader thinks the axis begins. Centred, the same script put a correspondence line off
       * the right-hand end of its own ground line.
       */
      const LEAD_IN = 40;
      const ends: EntityId[] = [];
      for (const side of [-LEAD_IN, length.value]) {
        counter += 1;
        const id = asEntityId(`s${String(counter)}`);
        document = addEntity(document, {
          ...createEntity(id, { op: 'free-point', position: vec.vec2(side, 0) }, style, {
            zIndex: counter,
          }),
          visible: false,
        });
        created += 1;
        ends.push(id);
      }
      const [left, right] = ends;
      if (left === undefined || right === undefined) return;
      counter += 1;
      const axis = asEntityId(`s${String(counter)}`);
      document = addEntity(
        document,
        createEntity(axis, { op: 'segment', a: left, b: right }, style, { zIndex: counter })
      );
      created += 1;
      if (name !== null) names.set(name, axis);

      // The `x` sits at the right-hand end, which is where every textbook puts it.
      counter += 1;
      document = addEntity(
        document,
        createEntity(
          asEntityId(`s${String(counter)}`),
          { op: 'text', anchor: right, offset: { x: 14, y: 0 }, content: 'x', latex: false },
          style,
          { zIndex: counter }
        )
      );
      created += 1;
      return;
    }

    if (keyword.value === 'epure') {
      const who = tokens[1];
      const numbers = tokens.slice(2);
      if (
        who?.kind !== 'word' ||
        numbers.length !== 3 ||
        numbers.some((token) => token.kind !== 'number')
      ) {
        fail(
          'epure takes a name and three numbers — along the axis, độ xa, độ cao: ' +
            '`epure A 60 30 40`'
        );
        return;
      }
      const [along, away, high] = numbers.map((token) => Number(token.value));
      if (along === undefined || away === undefined || high === undefined) return;

      const placed = placeEpure(who.value, along, away, high);
      if ('error' in placed) {
        fail(placed.error);
        return;
      }
      if (name !== null) names.set(name, placed.tie);
      return;
    }

    /*
     * `vtrace M A B` and `htrace N A B` — where a line pierces a plane of projection.
     *
     * The first construction of the subject after the epure itself, and the one that shows why
     * the coordinates had to be kept. A vết đứng is the point of the line whose độ xa is zero,
     * and a vết bằng the point whose độ cao is zero — each is one linear solve in space, and
     * neither can be read off two flat projections without undoing the projection first.
     *
     * The answer is a point like any other, so it is drawn like any other: both projections and
     * the correspondence line. What makes it recognisable as a trace is where they land — a
     * vertical trace has its *horizontal* projection on the axis, and a horizontal trace its
     * vertical one, because a coordinate of zero is exactly what the axis is.
     *
     * Refused when the line runs parallel to that plane, which is a real configuration and not
     * an error in the script: a line parallel to P1 never reaches it, so there is no trace to
     * name, and drawing one anyway would answer a question the figure was not asked.
     */
    if (keyword.value === 'vtrace' || keyword.value === 'htrace') {
      const vertical = keyword.value === 'vtrace';
      const [, who, first, second] = tokens;
      if (
        tokens.length !== 4 ||
        who?.kind !== 'word' ||
        first?.kind !== 'word' ||
        second?.kind !== 'word'
      ) {
        fail(
          `${keyword.value} takes a name for the trace and the two points of the line: ` +
            `\`${keyword.value} M A B\``
        );
        return;
      }

      const from = epures.get(first.value);
      const to = epures.get(second.value);
      if (from === undefined || to === undefined) {
        const unknown = from === undefined ? first.value : second.value;
        fail(
          `\`${unknown}\` is not a point of the two-view drawing — declare it with ` +
            '`epure` first, which is what knows where it is in space'
        );
        return;
      }

      // Zero of độ xa for a vertical trace, of độ cao for a horizontal one.
      const start = vertical ? from.away : from.high;
      const end = vertical ? to.away : to.high;
      if (Math.abs(end - start) < 1e-9) {
        fail(
          vertical
            ? `${first.value}${second.value} keeps the same độ xa, so it runs parallel to P1 and has no vết đứng`
            : `${first.value}${second.value} keeps the same độ cao, so it runs parallel to P2 and has no vết bằng`
        );
        return;
      }

      const t = start / (start - end);
      const at = {
        along: from.along + (to.along - from.along) * t,
        away: from.away + (to.away - from.away) * t,
        high: from.high + (to.high - from.high) * t,
      };
      // The coordinate solved for is zero; said outright so rounding cannot leave it just off.
      const traced = placeEpure(
        who.value,
        at.along,
        vertical ? 0 : at.away,
        vertical ? at.high : 0
      );
      if ('error' in traced) {
        fail(traced.error);
        return;
      }
      if (name !== null) names.set(name, traced.tie);
      return;
    }

    /*
     * `truelength T A B` — the right-triangle construction for độ dài thật.
     *
     * The oldest question in the subject: a segment in space is drawn twice and neither drawing
     * is its length. The construction is to take one projection as a leg, erect the *difference*
     * of the other coordinate as the second leg, and read the hypotenuse — so the drawing itself
     * carries the answer, which is why a figure is worth more here than a number.
     *
     * Built on the horizontal projection, with the difference in độ cao raised at `B2`. The
     * mirror construction on the vertical projection with độ xa is equally standard and gives
     * the same length; one of them has to be chosen, and this is the one the textbooks lead with.
     *
     * `T` is the far end of the raised leg, so `A2T` is the true length and can be measured,
     * styled or captioned like any other segment.
     */
    if (keyword.value === 'truelength') {
      const [, who, first, second] = tokens;
      if (
        tokens.length !== 4 ||
        who?.kind !== 'word' ||
        first?.kind !== 'word' ||
        second?.kind !== 'word'
      ) {
        fail('truelength takes a name and the two points of the segment: `truelength T A B`');
        return;
      }
      const fromName = first.value;
      const toName = second.value;
      const from = epures.get(fromName);
      const to = epures.get(toName);
      if (from === undefined || to === undefined) {
        const unknown = from === undefined ? fromName : toName;
        fail(
          `\`${unknown}\` is not a point of the two-view drawing — declare it with ` +
            '`epure` first, which is what knows where it is in space'
        );
        return;
      }
      if (names.has(who.value)) {
        fail(`the name \`${who.value}\` is already used — give the far end another`);
        return;
      }

      const a2 = vec.vec2(from.along, -from.away);
      const b2 = vec.vec2(to.along, -to.away);
      const run = vec.subtract(b2, a2);
      const length = vec.length(run);
      if (length < 1e-9) {
        fail(
          `${fromName} and ${toName} have the same hình chiếu bằng, so there is no leg to ` +
            'build the triangle on — the segment is vertical and its true length is the ' +
            'difference of the độ cao'
        );
        return;
      }

      /*
       * The raised leg is perpendicular to the projection and as long as the difference in
       * height. Which side it goes is a free choice; away from the axis keeps it clear of the
       * rest of the diagram, which is where a draughtsman puts it.
       */
      const rise = Math.abs(to.high - from.high);
      const perpendicular = vec.vec2(-run.y / length, run.x / length);
      const outward = perpendicular.y > 0 ? vec.scale(perpendicular, -1) : perpendicular;
      const far = vec.add(b2, vec.scale(outward, rise));

      counter += 1;
      const tip = asEntityId(`s${String(counter)}`);
      document = addEntity(document, {
        ...createEntity(tip, { op: 'free-point', position: far }, style, { zIndex: counter }),
        label: who.value,
      });
      created += 1;
      names.set(who.value, tip);

      const second2 = names.get(`${toName}2`);
      const first2 = names.get(`${fromName}2`);
      if (first2 === undefined || second2 === undefined) return;

      for (const [a, b, preset] of [
        [second2, tip, 'faint'],
        [first2, tip, 'accent'],
      ] as const) {
        counter += 1;
        document = addEntity(
          document,
          createEntity(
            asEntityId(`s${String(counter)}`),
            { op: 'segment', a, b },
            { ...style, ...(STYLE_PRESETS[preset] ?? {}) },
            { zIndex: counter }
          )
        );
        created += 1;
      }

      // The right angle at B2 is the whole justification of the construction, so it is drawn.
      counter += 1;
      const square = asEntityId(`s${String(counter)}`);
      document = addEntity(
        document,
        createEntity(
          square,
          { op: 'angle-mark', a: first2, vertex: second2, c: tip, ticks: 1, showValue: false },
          style,
          { zIndex: counter }
        )
      );
      created += 1;
      if (name !== null) names.set(name, square);
      return;
    }

    /*
     * `A = rim O 210` — a named point on a round solid's base circle.
     *
     * Separate from `cone`/`cylinder` because the *number* of rim points a problem needs is not
     * a property of the solid: one problem names two generators, another names four, another
     * none at all. Putting them in the solid statement would mean inventing default positions,
     * and a default position on a circle is a guess about a figure only the author has seen.
     *
     * The angle is measured in the base plane, from the front, anticlockwise seen from above.
     * That is a statement about the solid rather than about the page, which is the only kind a
     * projected figure lets you make safely.
     */
    if (keyword.value === 'rim') {
      const target = tokens[1];
      const degrees = tokens[2];
      const centre = String(target?.value);
      const home = solids.find((solid) => solid.rings.has(centre));

      if (name === null || degrees?.kind !== 'number' || home === undefined) {
        /*
         * The message names `on`, because reaching for `rim` on a circle drawn in the plane is
         * the commonest way to arrive here and "rim takes the centre of a round base" tells
         * someone what they did wrong without telling them what to write instead. It happened
         * twice in twelve problems, both times on a circle that was right there in the script.
         */
        fail(
          'rim takes the centre of a `cone` or `cylinder` base: `A = rim O 210`, ' +
            'or a sphere circle you have named: `sphere O … circle C 60` then `A = rim C 210`. ' +
            'For a circle drawn on the page use `on`: `A = on c 210`'
        );
        return;
      }

      /*
       * A construction rather than a computed position, which is the change. The angle is kept
       * and the point is worked out from the solid every time — so resizing the cone slides its
       * rim points round the new circle instead of leaving them off it.
       */
      counter += 1;
      const id = asEntityId(`s${String(counter)}`);
      document = addEntity(
        document,
        createEntity(
          id,
          {
            op: 'solid-rim-point',
            solid: home.id,
            ring: home.rings.get(centre) ?? 0,
            degrees: degrees.value,
          },
          style,
          { zIndex: counter }
        )
      );
      created += 1;
      names.set(name, id);
      return;
    }

    /*
     * `K = foot O S M` — the foot of the perpendicular from `O` to the line `SM`, in space.
     *
     * ## Why `project` is not enough on a solid
     *
     * Because an affine projection does not preserve perpendicularity. `project O SM` finds the
     * foot on the *page*, which is a different point from the foot in space — measured on the
     * cone figure, 0.8716 of the way along `SM` against the true 0.8738. Two tenths of a per
     * cent, invisible, and still the wrong point.
     *
     * The visible consequence is not the position but the *dashing*. A page-projected foot has
     * no place in space, so nothing can ask whether the solid covers the line drawn to it, and
     * `OK` came out solid on a figure where it runs through the middle of a cone. Computing the
     * foot in space gives it a position, and the opacity rule reaches it like any other point.
     *
     * Like `rim`, the result is a *placed* point rather than a live construction. Everything a
     * solid statement makes is placed — the solid itself is free points — so this is consistent
     * rather than a compromise, and it is the price of asking a metric question about a figure
     * whose projection has thrown the metric away.
     */
    if (keyword.value === 'foot') {
      const argued = tokens.slice(1, 4).map((token) => names.get(String(token.value)));
      const [from, a, b] = argued;

      /*
       * `foot B CD` — the line named rather than spelled out as two points.
       *
       * The same statement `project` already is, and accepted under this name because `foot` is
       * the word for it and a reader who has written `CD = segment C D` names it. Refusing was
       * asking someone to spell out two endpoints they had just given a name to.
       *
       * Only in a flat figure. A named segment of a solid is a drawn line, and a drawn line has
       * no position in space to be perpendicular in — the three-point form is what reaches the
       * solid, because the three points are the ones carrying their preimages.
       */
      /*
       * `H = foot S base` — a point and a **plane**.
       *
       * The form a solid-geometry paper actually uses: every "khoảng cách từ S đến (ABCD)" is
       * about this point, and the height of a pyramid is the segment from the apex to it. Taken
       * before the two-argument flat form below, and unambiguous beside it: that one is only
       * reached when the figure has no solid in it at all.
       */
      if (tokens.length === 3 && solids.length > 0) {
        const onto = a === undefined ? undefined : document.entities.get(a);
        if (onto?.source.op === 'plane' || onto?.source.op === 'plane-parallel') {
          if (name === null || from === undefined || a === undefined) {
            fail('foot takes a point and the plane it drops a perpendicular to: `H = foot S p`');
            return;
          }

          counter += 1;
          const at = asEntityId(`s${String(counter)}`);
          document = addEntity(
            document,
            createEntity(
              at,
              { op: 'plane-foot', solid: onto.source.solid, plane: a, from },
              style,
              { zIndex: counter }
            )
          );
          created += 1;
          names.set(name, at);
          return;
        }
      }

      if (tokens.length === 3 && solids.length === 0) {
        if (name === null || from === undefined || a === undefined) {
          fail('foot takes a point and the line it drops a perpendicular to: `H = foot B CD`');
          return;
        }
        counter += 1;
        const at = asEntityId(`s${String(counter)}`);
        document = addEntity(
          document,
          createEntity(at, { op: 'projected-point', point: from, onto: a }, style, {
            zIndex: counter,
          })
        );
        created += 1;
        names.set(name, at);
        return;
      }
      /*
       * Any solid will do as the frame. The three points carry their own positions in space —
       * that is what makes them solid points — and the figure is here only to say which
       * projection put them on the page. Whether they really belong to it is settled at
       * evaluation, where a point with no position in space simply yields nothing.
       */
      const home = solids[0];

      if (
        name === null ||
        argued.length !== 3 ||
        from === undefined ||
        a === undefined ||
        b === undefined
      ) {
        fail('foot takes a point and two more it drops a perpendicular to: `K = foot O S M`');
        return;
      }

      /*
       * In a flat figure there is no solid, and none is needed: the page *is* the plane, so the
       * foot of the perpendicular on the page is the foot, exactly. `foot B C D` becomes the
       * line CD and a projection onto it, with the line kept out of the picture — the reader
       * asked for a point, not for CD extended in both directions.
       *
       * Sugar rather than a source op, because `projected-point` already means this and a second
       * op meaning the same thing in the plane is how two answers to one question start to
       * disagree. It is here because every model handed the language reaches for `foot` in a
       * plane figure — it is the word for it — and being told to write `line` and then `project`
       * teaches nothing except that the language has a seam in it.
       */
      if (home === undefined) {
        counter += 1;
        const through = asEntityId(`s${String(counter)}`);
        document = addEntity(document, {
          ...createEntity(through, { op: 'line', a, b }, style, { zIndex: counter }),
          visible: false,
        });
        created += 1;

        counter += 1;
        const at = asEntityId(`s${String(counter)}`);
        document = addEntity(
          document,
          createEntity(at, { op: 'projected-point', point: from, onto: through }, style, {
            zIndex: counter,
          })
        );
        created += 1;
        names.set(name, at);
        return;
      }

      counter += 1;
      const id = asEntityId(`s${String(counter)}`);
      document = addEntity(
        document,
        createEntity(id, { op: 'solid-foot', solid: home.id, from, a, b }, style, {
          zIndex: counter,
        })
      );
      created += 1;
      names.set(name, id);
      return;
    }

    if (keyword.value === 'label') {
      const target = tokens[1];
      const content = tokens[2];
      if (target?.kind !== 'word' || content?.kind !== 'string') {
        fail('label takes an object and a quoted name: `label A "A"`');
        return;
      }
      const id = names.get(target.value);
      if (id === undefined) {
        fail(missing(target.value));
        return;
      }
      const entity = document.entities.get(id);
      if (entity === undefined) return;
      document = addEntity(
        { ...document, entities: new Map([...document.entities].filter(([key]) => key !== id)) },
        { ...entity, label: content.value }
      );
      return;
    }

    const spec = lookupStatement(keyword.value);
    if (spec === undefined) {
      fail(`unknown statement \`${keyword.value}\``);
      return;
    }

    const args = tokens.slice(1);
    const variadic = VARIADIC.has(spec.keyword);

    if (!variadic && args.length !== spec.parameters.length) {
      const expected = spec.parameters.map((parameter) => parameter.name).join(', ');
      fail(`\`${spec.keyword}\` takes ${String(spec.parameters.length)} (${expected})`);
      return;
    }

    /*
     * A plain loop rather than `forEach`, so that the first bad argument can leave the
     * statement immediately. Reporting a second complaint about the same line would be
     * noise: the first one is the reason it failed.
     */
    const resolved: unknown[] = [];

    for (const [position, token] of args.entries()) {
      const parameter = spec.parameters[position];
      const kind = parameter?.kind ?? 'ref'; // trailing variadic arguments are references
      const label = parameter?.name ?? 'argument';

      if (kind === 'number') {
        if (token.kind !== 'number') {
          fail(`\`${label}\` has to be a number`);
          return;
        }
        resolved.push(token.value);
        continue;
      }

      if (kind === 'string') {
        if (token.kind !== 'string') {
          fail(`\`${label}\` has to be quoted text`);
          return;
        }
        resolved.push(token.value);
        continue;
      }

      if (token.kind !== 'word') {
        fail('expected the name of an earlier object');
        return;
      }
      const id = names.get(token.value);
      if (id === undefined) {
        fail(missing(token.value));
        return;
      }
      resolved.push(id);
    }

    if (variadic && resolved.length < minimumFor(spec.keyword)) {
      fail(`\`${spec.keyword}\` needs at least ${String(minimumFor(spec.keyword))} arguments`);
      return;
    }

    if (spec.keyword === 'measure') {
      const what = resolved[0];
      if (typeof what !== 'string' || !MEASURES.includes(what)) {
        fail(`measure takes one of: ${MEASURES.join(', ')}`);
        return;
      }
    }

    let source = spec.build(resolved) as unknown as EntitySource;

    /*
     * A plane statement does not name its solid, and does not have to.
     *
     * `plane S A C` names three corners of one figure; asking which figure would be asking the
     * author to repeat something they have just said. So it is found from the points, by walking
     * back to whatever built them — which handles a midpoint of two corners, a foot, an
     * intersection, and every other derived point, without a case for each.
     *
     * Points belonging to two different solids are the real error, and it is reported as one
     * rather than silently picking the first: a plane through corners of two separate figures is
     * a sentence with no meaning, and quietly cutting one of them would be an invented answer.
     */
    if (
      source.op === 'plane' ||
      source.op === 'plane-parallel' ||
      source.op === 'plane-meet' ||
      source.op === 'dihedral-mark'
    ) {
      /*
       * A blackboard figure names its **viewpoint** where a solid figure names its body.
       *
       * `meet` and `dihedral` need that first parent for two reasons at once: it is the thing the
       * answer is clipped to, and it is where the frame comes from. On a solid it is the solid.
       * With no solid there is nothing to clip *to* — the two planes clip each other — and the
       * frame is the space, so the space is what the edge points at. The edge matters beyond
       * tidiness: it is what makes the giao tuyến vanish when the figure does.
       */
      const frame = spaceBehindAll(document, parentsOf(source));
      const found = frame ?? solidBehindAll(document, parentsOf(source));
      if (found === null) {
        fail(
          source.op === 'plane-meet' || source.op === 'dihedral-mark'
            ? 'both planes have to belong to the same figure'
            : 'a plane is named by points of one solid — check they all belong to the same figure'
        );
        return;
      }
      source = { ...source, solid: found };
    }

    /*
     * A blackboard plane brings the figure's viewpoint with it, and only the first one does.
     *
     * The same arrangement `graph` has with its axes, for the same reason: a curve on a blank page
     * says nothing about where the origin is, and a plane on a blank page says nothing about which
     * way the figure is being looked at. Leaving it to the author would mean a statement nobody
     * would think to write, and a model would never write at all.
     *
     * **One** viewpoint, shared. Two planes drawn through different projections make a figure that
     * is geometrically false and looks perfectly normal, which is the failure ADR-0003 exists to
     * prevent — so a second `plane3` joins the first rather than bringing another.
     *
     * The anchor is hidden, like the point a toolbar solid hangs from. A viewpoint is not a thing
     * on the page, and a dot appearing where the author asked for a plane reads as a mistake.
     */
    if (source.op === 'plane-free') {
      let space = [...document.entities.entries()].find(
        ([, entity]) => entity.source.op === 'space'
      )?.[0];

      if (space === undefined) {
        counter += 1;
        const anchor = asEntityId(`s${String(counter)}`);
        document = addEntity(
          document,
          createEntity(anchor, { op: 'free-point', position: { x: 0, y: 0 } }, style, {
            visible: false,
            isConstruction: true,
          })
        );

        counter += 1;
        space = asEntityId(`s${String(counter)}`);
        document = addEntity(
          document,
          createEntity(
            space,
            {
              op: 'space',
              center: anchor,
              rotation: NO_ROTATION,
              projection: ProjectionKind.Isometric,
            },
            style,
            { visible: false, isConstruction: true }
          )
        );
        created += 2;
      }

      source = { ...source, space };
    }

    /*
     * A turned plane takes its solid from the plane it turns, and deliberately not from its points.
     *
     * `solidBehindAll` **skips** anything that traces back to no solid rather than refusing it, so
     * a pair of points where one is a free mark would still answer — with a "corner" that has no
     * position in space. The base plane cannot be that: it already knows its own solid, having
     * been cut into one, so asking it is both shorter and impossible to fool.
     */
    if (source.op === 'plane-turn') {
      const found = solidBehind(document, source.from);
      if (found === null) {
        fail('the first thing must be a plane already cut into a solid');
        return;
      }
      source = { ...source, solid: found };
    }

    /*
     * A formula that draws nothing is a failed line, not an invisible object.
     *
     * The same rule the toolbar follows, and for the same reason: an entity in the object list
     * that draws nothing is a puzzle with no solution. Here it matters more, because the author
     * is a model that will read the error and try again — `graph "x^" -1 5` has to come back as
     * a complaint about that line rather than as a figure with a curve quietly missing from it.
     */
    if (spec.keyword === 'graph' && evaluate(source, []) === null) {
      fail('that formula draws nothing — check it reads as `y = f(x)`, e.g. `x^2-4x+3`');
      return;
    }

    /*
     * The same segment written twice, in either order.
     *
     * A model wrote `MB = segment M B` and then `BM = segment B M` four lines later, which draws
     * one line and puts two objects in the list. Nothing looked wrong, and `noAmbiguousCorner`
     * reported two lines leaving a point 0.0° apart — true, unhelpful, and only visible because
     * the corpus happened to be measuring for a different reason.
     *
     * A parse error rather than a rule in the instructions, because the parser can see it: it
     * arrives exactly when it happens, costs nothing when it does not, and the endpoint sends it
     * back for the corrective turn. It suppresses no ink — the second line is drawn precisely
     * underneath the first — so the one rule this project holds against silent removal does not
     * apply, and the message names the object that is already there rather than inviting a
     * rename.
     */
    if (spec.keyword === 'segment') {
      const [a, b] = resolved as readonly (EntityId | undefined)[];
      const already =
        a === undefined || b === undefined
          ? undefined
          : segments.get(a < b ? `${a}|${b}` : `${b}|${a}`);
      if (already !== undefined) {
        fail(`that segment is already drawn as \`${already}\` — delete this line`);
        return;
      }
    }

    counter += 1;
    const id = asEntityId(`s${String(counter)}`);

    if (spec.keyword === 'segment') {
      const [a, b] = resolved as readonly (EntityId | undefined)[];
      if (a !== undefined && b !== undefined) {
        segments.set(a < b ? `${a}|${b}` : `${b}|${a}`, name ?? `${String(a)}${String(b)}`);
      }
    }

    /*
     * Nothing about the solid is decided here any more, and that is the change.
     *
     * Three things used to be worked out while the script ran and written into the drawing:
     * which lines the solid covers, which angle marks are square, and where a point sits in
     * space. All three were right exactly once. Drag a corner and the shape moved while the
     * answers stayed put — which ADR-0003 warns against in as many words: a source says how an
     * entity is *determined*, never what it currently looks like.
     *
     * They are derived now. A point that came from a solid carries its position in space in its
     * *value*, so `midpoint` and `divide` pass it on for nothing; the angle mark reads it when
     * it decides squareness; and the renderer asks each solid whether it covers a line. Every
     * one of them is recomputed when anything moves.
     */
    /*
     * A region whose corners are in space, but not on one plane.
     *
     * This is the defect that made planes worth building. `polygon A M N D` through four corners
     * of a cube shades exactly as convincingly whether they lie on a common plane or sixty-seven
     * units away from one, and nothing said a word — so a figure could be geometrically
     * impossible and look perfect, which is the one thing ADR-0003 exists to prevent.
     *
     * Refused rather than drawn with a warning. A shaded quadrilateral is a *claim* about the
     * solid, and a wrong one belongs in no worksheet; the message says how far out it is, so the
     * author knows which corner they mistyped, and points at `plane`, which cannot be wrong.
     *
     * Only when every corner knows where it is. A flat figure's polygon is a region on the page
     * and has no plane to be off.
     */
    if (source.op === 'polygon') {
      const corners = source.vertices.map((vertex) => {
        const value = valueOf(vertex);
        return value?.kind === 'point' ? value.in3d : undefined;
      });

      if (corners.length > 3 && corners.every((corner) => corner !== undefined)) {
        const out = offPlane(corners);
        if (!coplanar(corners)) {
          fail(
            `those corners are ${out.toFixed(1)} out of any one plane, so they are not a section — ` +
              'use `plane` through three of them and let the section be worked out'
          );
          return;
        }
      }
    }

    /*
     * A plane arrives already toned.
     *
     * Writing `style p plane` after every plane is a line that says nothing a reader needs — the
     * statement already said it is a plane. It stays overridable, because `style` is applied
     * afterwards and the last word wins, and a teacher who wants two planes told apart by colour
     * still says so once each.
     */
    /*
     * A plane arrives toned, and the *second* one in a figure differently from the first.
     *
     * `STYLE_PRESETS.plane` stays what it is — the look the word `plane` names, for an author who
     * writes `style p plane` and means one definite thing. This is the **default** a plane gets
     * when nobody has said anything, and it alternates so that two planes drawn to be seen
     * crossing can be told apart where they cross.
     */
    const planesSoFar = [...document.entities.values()].filter((entity) =>
      isPlaneSource(entity.source)
    ).length;
    const applied = isPlaneSource(source)
      ? { ...style, ...STYLE_PRESETS['plane'], ...toneForPlane(planesSoFar) }
      : style;

    /*
     * The frame comes from the *curve*, not from the interval alone.
     *
     * `max(|from|, |to|)` was the whole rule, so a cubic over [-2.5, 2.5] was given a window
     * reaching 2.5 while the curve reached ±9 — and what came out was three fragments a class
     * cannot read as one function. See `graphWindow`.
     *
     * Computed before the curve is added rather than after, because the curve needs it too: the
     * y unit belongs to the *pair*, and the only way a pair agrees is by one number being written
     * into both of them at once.
     */
    const window =
      spec.keyword === 'graph'
        ? graphWindow(String(resolved[0]), Number(resolved[1]), Number(resolved[2]))
        : null;

    /*
     * A steep curve is drawn against a shorter y unit, and the axes are told the same number.
     *
     * Without it, `graph "x^3-3x" -3 3` is 20 units wide and 160 tall, and the figure that comes
     * out is a tower nothing can be printed at — the defect the toolbar's `addFunctionGraph`
     * already avoids, which the script had simply never been given.
     *
     * Only when this curve is the one bringing the axes. A `graph` under an `axes` statement must
     * match the axes that are *there*, and a script-written `axes` says nothing about a y unit, so
     * it draws at `scale` both ways and the curve must too. Compressing against a window of its
     * own would put the curve on a scale the drawn numbers do not use — which is the one thing a
     * graph may not do, and is pinned in `construction/graphUnits.test.ts`.
     */
    const brings = spec.keyword === 'graph' && !hasAxes(document) && window !== null;
    const drawn =
      brings && source.op === 'function-graph'
        ? { ...source, scaleY: DEFAULT_GRAPH_SCALE * window.yScale }
        : source;

    document = addEntity(document, createEntity(id, drawn, applied, { zIndex: counter }));
    created += 1;
    if (name !== null) names.set(name, id);

    /*
     * A graph brings axes, exactly as `addFunctionGraph` does on the toolbar.
     *
     * Not tidiness, and not something to leave to whoever wrote the script: a curve on a blank
     * page says nothing at all about where the origin is or what one unit means, so a figure
     * that omits them is not a simpler drawing of the same thing. A model that forgets the line
     * produces a squiggle, and it has no way of finding out.
     *
     * Only when there are none already — a second pair drawn over the first reads as a printing
     * fault — and behind everything, so the curve is drawn over its own frame of reference.
     */
    /*
     * The window is also what the asymptotes below are cut to: a dashed line longer than the
     * frame drags the whole export out with it.
     */
    if (brings) {
      counter += 1;
      const axes = asEntityId(`s${String(counter)}`);

      document = addEntity(
        document,
        createEntity(
          axes,
          {
            op: 'axes',
            scale: DEFAULT_GRAPH_SCALE,
            scaleY: DEFAULT_GRAPH_SCALE * window.yScale,
            extent: window.extentX,
            extentY: window.extentY,
            center: window.center,
            step: window.step,
            stepY: window.stepY,
            numbered: true,
          },
          // Grey and thin: a frame of reference is read *through*, and axes as heavy as the
          // curve make a graph that is tiring to look at.
          { ...style, stroke: '#6b7280', strokeWidth: 1 },
          { zIndex: -1 }
        )
      );
      created += 1;
    }

    /*
     * The asymptotes, drawn dashed, because for one chapter of the syllabus they *are* the figure.
     *
     * `y = (2x+1)/(x−1)` without them is two arcs floating on a page — nothing says where they
     * are going, and the exam question "cho đồ thị như hình vẽ, tìm tiệm cận đứng" cannot be set
     * from it. They were reachable before only as a trick: a line through two plotted points with
     * the `faint` style. A trick is no use to a teacher who does not know it exists, nor to a
     * model turning a problem statement into a script, which will not invent one.
     *
     * Real line entities rather than something the graph draws itself, so every renderer, the
     * exports, the layers list and `hide` all work on them already — and a teacher who does not
     * want one can remove it like anything else on the page.
     *
     * One that lies along an axis is skipped: `y = 1/x` has both, and drawing them would be two
     * dashed strokes down the middle of the axes that are already there.
     */
    if (spec.keyword === 'graph' && window !== null) {
      let parsedFormula;
      try {
        parsedFormula = parseExpression(String(resolved[0]));
      } catch {
        parsedFormula = null;
      }

      if (parsedFormula !== null) {
        const from = Number(resolved[1]);
        const to = Number(resolved[2]);

        /*
         * As long as the frame and no longer.
         *
         * The first cut drew each asymptote across four times the interval, and the export frame
         * — which is fitted to the *content* — grew to hold it. The figure came out with the
         * axes floating in the middle of a much larger page. An asymptote is an annotation on
         * this graph; it should reach the edge of the drawing and stop, exactly as a printed one
         * does.
         */
        const box = windowBox(window);
        // A hair past the frame, so a dashed line ends *at* the edge rather than a pixel inside
        // it. Measured from the frame's own edges, which are no longer symmetric about the axes.
        const over = 0.04;
        const reach = {
          left: box.minX - window.extentX * over,
          right: box.maxX + window.extentX * over,
          bottom: box.minY - window.extentY * over,
          top: box.maxY + window.extentY * over,
        };

        for (const line of asymptotesOf(parsedFormula, from, to)) {
          const onAnAxis =
            (line.kind === 'vertical' && Math.abs(line.at) < 1e-9) ||
            (line.kind === 'horizontal' && Math.abs(line.at) < 1e-9);
          if (onAnAxis) continue;

          const s = DEFAULT_GRAPH_SCALE;
          const ends =
            line.kind === 'vertical'
              ? ([
                  { x: line.at * s, y: reach.bottom * s },
                  { x: line.at * s, y: reach.top * s },
                ] as const)
              : ([
                  { x: reach.left * s, y: (line.at + (line.slope ?? 0) * reach.left) * s },
                  { x: reach.right * s, y: (line.at + (line.slope ?? 0) * reach.right) * s },
                ] as const);

          /*
           * Two ends, named rather than collected.
           *
           * A loop into an array meant reading `made[0]` and `made[1]` back out, and under
           * `noUncheckedIndexedAccess` there is no honest way to do that — the two lint rules
           * that fire on it disagree with each other, which is the language pointing out that
           * the shape is wrong. A line has exactly two ends; naming them says so.
           *
           * The points are invisible: they are scaffolding for an infinite line, and a dot at
           * each would read as a point *on* the graph, which is precisely what an asymptote
           * is not.
           */
          const endPoint = (at: { readonly x: number; readonly y: number }): EntityId => {
            counter += 1;
            const id = asEntityId(`s${String(counter)}`);
            document = addEntity(document, {
              ...createEntity(id, { op: 'free-point', position: at }, style),
              visible: false,
            });
            created += 1;
            return id;
          };

          const [first, second] = ends;
          const a = endPoint(first);
          const b = endPoint(second);

          counter += 1;
          document = addEntity(
            document,
            createEntity(
              asEntityId(`s${String(counter)}`),
              { op: 'line', a, b },
              { ...style, ...(STYLE_PRESETS['faint'] ?? {}) }
            )
          );
          created += 1;
        }
      }
    }
  });

  return { document, errors, created, names };
}

/** Whether the drawing already has a frame of reference to read a graph against. */
function hasAxes(document: SceneDocument): boolean {
  return axesSource(document) !== null;
}

/** The drawing's frame of reference, when it has one. */
function axesSource(document: SceneDocument): AxesSource | null {
  for (const entity of document.entities.values()) {
    if (entity.source.op === 'axes') return entity.source;
  }
  return null;
}

/**
 * Reads `A B C . A2 B2 C2 at 0 0 size 240 220 110`, or says what is wrong with it.
 *
 * Returns the message rather than throwing, because every other failure in this parser is a
 * line-numbered complaint the writer can act on and an exception here would be the one that
 * takes the whole script down.
 */
/**
 * What a solid statement describes, as the document will store it.
 *
 * A *description*, not a drawing. The corners, the hidden edges and everything built on them
 * come back out of these numbers whenever anything changes — which is what lets the figure
 * survive being adjusted, and is what ADR-0003 has always asked of a source.
 *
 * `order` is the corner names in the numbering the solid itself uses, so a `solid-vertex` can be
 * an index rather than a name and the document never carries the words twice.
 */
interface SolidSpec {
  readonly solid: 'prism' | 'pyramid' | 'cone' | 'cylinder' | 'sphere';
  readonly at: Vec2;
  /**
   * The point the solid is centred on, when the script named one instead of giving coordinates.
   *
   * `at 0 0` makes a hidden free point and hangs the solid off it; `at I` hangs it off a point
   * that is already there. The difference matters for one figure in particular. "Mặt cầu ngoại
   * tiếp hình chóp" puts the centre at a *derived* place — the midpoint of SC, the circumcentre
   * of a face — and with coordinates only, that sphere could not be drawn at all unless the
   * centre happened to land where the solid's own centre already was. A cube obliges; nothing
   * else does.
   *
   * It also comes out live, which is the better half. The origin is read from the parent at
   * evaluation, so resizing the pyramid moves `I = midpoint S C`, and the sphere follows.
   */
  readonly atName?: string;
  /** Planes cutting a sphere. See `SphereSection`. */
  readonly sections?: readonly SphereSection[];
  readonly width: number;
  readonly height: number;
  readonly depth: number;
  readonly corners: number;
  readonly foot: readonly number[];
  /** The base corner whose angle the problem stated, the angle, and a trapezium's far side. */
  readonly baseAngleAt?: number;
  readonly baseAngle?: number;
  readonly baseFar?: number;
  readonly baseFarOffset?: number;
  /** The base given corner by corner, flat, when the script listed it. */
  readonly baseCorners?: readonly number[];
  /**
   * The corner names in the solid's own numbering, or `null` for a vertex the script did not name.
   *
   * A hole rather than a shorter list, because the position *is* the index: `solid-vertex` stores
   * a number and the solid hands back its vertices in one fixed order. A sphere cut by two planes
   * where only the second was named has a real vertex at the first — nothing should be drawn
   * there, and nothing after it should shift up a place either.
   */
  readonly order: readonly (string | null)[];
  /** Which base circle each named centre owns, for `rim`. Empty for a prism or a pyramid. */
  readonly rings: ReadonlyMap<string, number>;
}

/**
 * A solid too small to carry its own labels.
 *
 * These numbers are *drawing* units and the document has no scale, so nothing about a tiny solid
 * is wrong — until it is exported. An export frames the content and then writes the names at a
 * fixed size, so a room given as `size 4 3.2 4` because the problem says four metres comes out as
 * a 106-pixel picture with the letters larger than the room.
 *
 * Judged on the largest dimension: a solid thin in one direction is the flatness rule's business,
 * and this one is about the figure as a whole.
 */
function illegible(width: number, height: number, depth: number): string | null {
  const legible = 40;
  const largest = Math.max(width, height, depth);
  return largest < legible
    ? `${largest.toFixed(1)} is too small to label: these are drawing units, not the metres in ` +
        'the problem. Keep the proportions and make the figure a few hundred across'
    : null;
}

/**
 * What follows `at`: two numbers, or the name of a point already built.
 *
 * One reader for all four solid statements, because a rule that holds for a sphere and not for a
 * cone is a rule nobody can remember. It reports which of the two was written and leaves the
 * looking-up to the caller, which is the only place that has the names in scope.
 */
function anchorOf(
  tokens: readonly Token[],
  at: number,
  size: number
): { readonly name: string | null } | string {
  const between = tokens.slice(at + 1, size);
  const only = between[0];
  if (between.length === 1 && only?.kind === 'word') return { name: only.value };
  if (between.length === 2 && between.every((token) => token.kind === 'number')) {
    return { name: null };
  }
  return '`at` takes two numbers or the name of a point already built: `at 0 0`, or `at I`';
}

function buildSolid(keyword: string, tokens: readonly Token[]): SolidSpec | string {
  /*
   * A round base is a circle, so it has one measurement across and not two. Asking for a width
   * and a depth would let a script state an elliptical cone — a shape no problem in the book
   * mentions and one whose figure would be read as a mistake.
   */
  const isRound = ROUND.has(keyword);
  const isBall = keyword === BALL;
  const shape = isBall
    ? `\`${keyword} O at 0 0 size 320\``
    : isRound
      ? `\`${keyword} ${keyword === 'cone' ? 'S O' : 'O O2'} at 0 0 size 360 230\``
      : `\`${keyword} ${
          keyword === 'prism' ? 'A B C . A2 B2 C2' : 'S . A B C D'
        } at 0 0 size 240 220 110\``;

  const words = tokens.map((token) => (token.kind === 'word' ? token.value : null));
  const dot = words.indexOf('.');
  const at = words.indexOf('at');
  const size = words.indexOf('size');

  if (at === -1 || size === -1 || at > size) {
    return `${keyword} needs \`at\` and \`size\`, in that order: ${shape}`;
  }

  /*
   * Checked before the anchor is read, because `over` written early lands *between* `at` and
   * `size` and would otherwise be reported as a malformed `at`. "`over` comes last" names the
   * repair; "`at` takes two numbers" sends the writer to fix the line that is already correct.
   */
  const overAt = words.indexOf('over');
  if (overAt !== -1 && overAt < size) {
    return `\`over\` comes last, after the size: \`${shape.slice(1, -1)} over A\``;
  }

  const anchor = anchorOf(tokens, at, size);
  if (typeof anchor === 'string') return anchor;
  /* How many tokens `at` consumed: two coordinates, or the one name of a point. */
  const placed = anchor.name === null ? 2 : 1;
  const centred = anchor.name === null ? {} : { atName: anchor.name };

  /*
   * A sphere: one name, one number. Handled before the round pair because it shares their
   * shape and none of their arithmetic — there is no second axis to be foreshortened, no rim
   * to take an angle around, and no second face to name.
   */
  if (isBall) {
    if (dot !== -1) return `a sphere has no rings to separate, so no \`.\`: ${shape}`;

    /*
     * The name is optional, and only here. `sphere O at 0 0 size 320` names its own centre;
     * `sphere at I size 320` is centred on a point that already has a name, and giving it a
     * second one would put two labels on one dot and trip `noCoincidentPoints` — correctly,
     * because they really are the same point.
     */
    const named = tokens.slice(0, at);
    const centreToken = named[0];
    if (named.length > 1 || (named.length === 1 && centreToken?.kind !== 'word')) {
      return `a sphere takes the name of its centre, or none when \`at\` gives one: ${shape}`;
    }
    if (named.length === 0 && anchor.name === null) {
      return `a sphere needs a centre to be named, either before \`at\` or by it: ${shape}`;
    }

    /*
     * `circle <d>` — the plane cutting the ball, as its signed distance from the centre.
     *
     * Read out of the measures before they are counted, because everything below counts on the
     * sphere having exactly one number. Repeatable: "hai mặt phẳng song song cắt mặt cầu" is a
     * real question and two circles is the figure for it.
     */
    const rest = [...tokens.slice(at + 1 + placed, size), ...tokens.slice(size + 1)];
    const sections: SphereSection[] = [];
    const sectionNames = new Map<string, number>();
    const measures: typeof rest = [];
    for (let i = 0; i < rest.length; i++) {
      const token = rest[i];
      if (token?.kind === 'word' && token.value === 'circle') {
        /*
         * `circle C 60` — the name is optional and it is what makes the circle an *object*.
         *
         * Without one the ring is drawn and nothing else; with one, `rim C 90` puts a point on
         * it, which is how "tam giác ABC nội tiếp đường tròn giao tuyến" gets its three corners.
         * Optional rather than required because plenty of figures only need the circle drawn,
         * and a name nobody uses is a label on the page nobody asked for.
         */
        const maybeName = rest[i + 1];
        const named = maybeName?.kind === 'word';
        let step = named ? 2 : 1;
        const height = rest[i + step];
        if (height?.kind !== 'number') {
          return `\`circle\` takes an optional name then the distance from the centre: ${shape}`;
        }
        if (named) {
          const word = maybeName.value;
          if (sectionNames.has(word)) {
            return `two circles cannot both be called \`${word}\`: ${shape}`;
          }
          sectionNames.set(word, sections.length);
        }

        /*
         * `tilt 30` — how far the plane leans off horizontal.
         *
         * Optional, and left out of nearly every figure: a plane "cách tâm O một khoảng d" is
         * drawn level, and that is the picture the question expects. The clause is for the ones
         * that are not, and it is bounded well short of a right angle — at 90° the plane contains
         * the axis and the ring is seen edge on, which is a line rather than a circle and reads
         * as a mistake rather than as a figure.
         */
        const maybeTilt = rest[i + step + 1];
        let tilt = 0;
        if (maybeTilt?.kind === 'word' && maybeTilt.value === 'tilt') {
          const degrees = rest[i + step + 2];
          if (degrees?.kind !== 'number') {
            return `\`tilt\` takes the angle the plane leans off horizontal: ${shape}`;
          }
          if (Math.abs(degrees.value) >= 90) {
            return `\`tilt ${String(degrees.value)}\` stands the plane on edge, so there is no circle to draw: ${shape}`;
          }
          tilt = degrees.value;
          step += 2;
        }

        sections.push(tilt === 0 ? { at: height.value } : { at: height.value, tilt });
        i += step;
        continue;
      }
      if (token !== undefined) measures.push(token);
    }
    if (measures.length !== 1 || measures.some((token) => token.kind !== 'number')) {
      return `a sphere takes \`at x y\` and one \`size\`, its diameter: ${shape}`;
    }
    const coordinates = tokens.slice(at + 1, size).map((token) => Number(token.value));
    const across = Number(measures[0]?.value);
    if (!Number.isFinite(across)) {
      return `a sphere takes \`at x y\` and one \`size\`, its diameter: ${shape}`;
    }
    if (across <= 0) return 'a sphere needs a positive diameter';

    const tiny = illegible(across, across, across);
    if (tiny !== null) return tiny;

    /*
     * A plane further from the centre than the radius does not cut the ball, and one exactly at
     * the radius touches it in a point. Refused rather than drawn as nothing, because a figure
     * silently missing the circle the question is about is the failure that costs the most: the
     * teacher sees a sphere, assumes the line was ignored, and goes back to drawing by hand.
     */
    for (const section of sections) {
      if (Math.abs(section.at) >= across / 2) {
        return `\`circle ${String(section.at)}\` is not inside a sphere of diameter ${String(across)}`;
      }
    }

    return {
      solid: 'sphere',
      ...(sections.length === 0 ? {} : { sections }),
      at: vec.vec2(coordinates[0] ?? 0, coordinates[1] ?? 0),
      ...centred,
      width: across,
      height: across,
      depth: across,
      corners: 0,
      foot: [],
      /*
       * The ball's centre, then one place per cutting plane — named when the script named it.
       *
       * The sphere's own centre may itself be unnamed: `sphere at I size 320` hangs the ball off
       * a point that already has a name, and giving it a second would put two labels on one dot.
       * Its place in the numbering is still taken, or every section below it would be off by one.
       */
      order: [
        centreToken === undefined ? null : String(centreToken.value),
        ...sections.map((_, index) => {
          for (const [word, at] of sectionNames) if (at === index) return word;
          return null;
        }),
      ],
      /*
       * Only the circles a plane cut out, never the sphere itself.
       *
       * `rim O 90` still refuses, and should: every great circle of a sphere is as good as every
       * other, so an angle round "the" rim names nothing. A **section** circle is different — the
       * problem chose its plane, so there is one circle to be at 90° of.
       */
      rings: sectionNames,
    };
  }

  if (isRound) {
    if (dot !== -1) return `${keyword} has no rings to separate, so no \`.\`: ${shape}`;

    const named = tokens.slice(0, at);
    if (named.some((token) => token.kind !== 'word')) {
      return `${keyword} takes names, not numbers: ${shape}`;
    }
    const measures = [...tokens.slice(at + 1 + placed, size), ...tokens.slice(size + 1)];
    if (measures.length !== 2 || measures.some((token) => token.kind !== 'number')) {
      return `${keyword} takes \`at x y\` and \`size diameter height\`: ${shape}`;
    }
    const coordinates = tokens.slice(at + 1, size).map((token) => Number(token.value));
    const [x, y] = [coordinates[0] ?? 0, coordinates[1] ?? 0];
    const [across, tall] = measures.map((token) => Number(token.value));
    if (across === undefined || tall === undefined) {
      return `${keyword} takes \`at x y\` and \`size diameter height\`: ${shape}`;
    }
    if (across <= 0 || tall <= 0) return `${keyword} needs a positive diameter and height`;

    /*
     * The same floor as a flat solid, and it has to be repeated because a round one is validated
     * and returned before that code is reached. It was missing here: `cone S O at 0 0 size 4 3.2`
     * — a cone described in metres — went straight through while the identical mistake on a prism
     * was refused.
     */
    const tiny = illegible(across, tall, across);
    if (tiny !== null) return tiny;

    const place = { at: vec.vec2(x, y), width: across, height: tall, depth: across };
    const who = named.map((token) => String(token.value));
    if (new Set(who).size !== who.length) return `${keyword} names a point twice`;

    if (keyword === 'cone') {
      const [apex, centre] = who;
      if (who.length !== 2 || apex === undefined || centre === undefined) {
        return `a cone takes its apex and the centre of its base: ${shape}`;
      }
      return {
        solid: 'cone',
        at: place.at,
        ...centred,
        width: across,
        height: tall,
        depth: across,
        corners: 0,
        foot: [],
        order: [apex, centre],
        rings: new Map([[centre, 0]]),
      };
    }

    const [bottom, top] = who;
    if (who.length !== 2 || bottom === undefined || top === undefined) {
      return `a cylinder takes the centres of its two faces: ${shape}`;
    }
    return {
      solid: 'cylinder',
      at: place.at,
      ...centred,
      width: across,
      height: tall,
      depth: across,
      corners: 0,
      foot: [],
      order: [bottom, top],
      rings: new Map([
        [bottom, 0],
        [top, 1],
      ]),
    };
  }

  if (dot === -1 || !(dot < at)) {
    return `${keyword} needs \`.\`, \`at\` and \`size\`, in that order: ${shape}`;
  }

  const before = tokens.slice(0, dot);
  const after = tokens.slice(dot + 1, at);
  if (
    before.some((token) => token.kind !== 'word') ||
    after.some((token) => token.kind !== 'word')
  ) {
    return `${keyword} takes corner names, not numbers: ${shape}`;
  }

  const left = before.map((token) => String(token.value));
  const right = after.map((token) => String(token.value));

  /*
   * `over` is optional and closes the line, so the size numbers stop where it starts.
   *
   * It used to be pyramid-only, on the reasoning that "a prism's second ring is the first one
   * translated, and `over` would have no meaning there that `at` does not already cover". The
   * first half is true and the second does not follow: a translation has a *direction*, and an
   * oblique prism is exactly one whose direction is not straight up. `at` moves the whole solid
   * and cannot say anything about it.
   *
   * What the omission cost was a whole family of problems — "lăng trụ xiên", and any prism whose
   * lateral edge is stated through the foot of a perpendicular. With nothing to write, a model
   * places the six corners by hand at coordinates it has projected itself, and every position in
   * space is lost with them: no edge can be dashed, and any angle mark measures the page.
   */
  const over = words.indexOf('over');

  /*
   * `angle A 60` — the base has that angle at that corner, so it is a right-angled triangle, a
   * rhombus or a parallelogram rather than the regular shape it would be otherwise.
   *
   * `right B` is the same clause with the angle already filled in. It stays because "đáy là tam
   * giác vuông tại B" is the commonest case by a wide margin and `angle B 90` reads like
   * arithmetic; it is sugar and not a second mechanism, so the two cannot drift.
   *
   * Three or four corners. Five or more is a regular polygon, where one stated angle does not
   * describe a shape — it would have to be a list of them, and no problem in the book asks.
   */
  const square = words.indexOf('right');
  const angled = words.indexOf('angle');
  const flush = words.indexOf('trapezoid');
  const even = words.indexOf('isosceles');
  const listed = words.indexOf('base');
  const sloped = flush === -1 ? even : flush;

  /*
   * One clause, because all three describe the same thing — the shape of the base — and two of
   * them at once would be either a repetition or a contradiction, with no way to tell which was
   * meant. `trapezoid A 420` is a right-angled one, which is the "hình thang vuông tại A và D"
   * of the book; a sloping trapezium would need an angle as well and no problem asks for one.
   */
  const shapes = [square, angled, flush, even, listed].filter((found) => found !== -1);
  if (shapes.length > 1) {
    return 'a base takes one of `right`, `angle`, `trapezoid`, `isosceles` or `base`, not more';
  }

  const clauseAt = shapes[0] ?? -1;
  const word =
    square !== -1
      ? 'right'
      : angled !== -1
        ? 'angle'
        : flush !== -1
          ? 'trapezoid'
          : even !== -1
            ? 'isosceles'
            : 'base';
  const example =
    square !== -1
      ? 'right B'
      : angled !== -1
        ? 'angle A 60'
        : flush !== -1
          ? 'trapezoid A 420'
          : even !== -1
            ? 'isosceles A 420'
            : 'base 0 0 200 0 260 120';

  if (clauseAt !== -1 && clauseAt < size) {
    return `\`${word}\` comes after \`size\`: \`${shape.slice(1, -1)} ${example}\``;
  }
  if (sloped !== -1 && after.length !== 4) {
    // Three corners cannot have two parallel sides, and the shape a writer meant is anybody's
    // guess — quietly building a triangle under the word "trapezoid" is the worst of the options.
    return `\`${word}\` describes a four-cornered base, and this one has ${String(after.length)}`;
  }
  if (clauseAt !== -1 && listed === -1 && after.length !== 3 && after.length !== 4) {
    return `\`${word}\` describes a three- or four-cornered base, and this one has ${String(after.length)}`;
  }

  /*
   * `angle` carries its own number, and it has to be read here rather than left to the size
   * numbers — which stop at the clause — or a figure would come out with the angle silently
   * dropped and the default shape drawn under the caption that asked for something else.
   */
  /*
   * A trapezium may state its angle too, as a third number: `trapezoid A 420 60` leans the
   * parallels over at 60° to the side between them, which is the angle at the named corner.
   *
   * `isosceles` may not, and that is geometry rather than policy. Its two legs are mirror images
   * of each other only when the axis of symmetry is perpendicular to the parallels; lean it over
   * and the legs come out 199 against 246 at 75°, which is a trapezium captioned "cân" that
   * plainly is not one.
   */
  const leaning = sloped !== -1 && tokens[sloped + 3]?.kind === 'number';
  if (leaning && even !== -1) {
    return 'an isosceles trapezium is symmetric, so its angle is not free: use `trapezoid` to lean one over';
  }

  const stated =
    angled !== -1
      ? Number(tokens[angled + 2]?.value)
      : leaning
        ? Number(tokens[sloped + 3]?.value)
        : 90;
  if (angled !== -1 && (tokens[angled + 2]?.kind !== 'number' || !Number.isFinite(stated))) {
    return '`angle` takes a corner and a number of degrees: `angle A 60`';
  }
  if (leaning && (stated <= 0 || stated >= 180)) {
    return `a trapezium's angle must be more than 0 and less than 180 degrees, not ${String(stated)}`;
  }
  if (angled !== -1 && (stated <= 0 || stated >= 180)) {
    // A corner of nothing or of a straight line is not a corner, and the base collapses to a
    // segment whose faces have no normal — the whole solid then draws as though it were behind
    // itself.
    return `\`angle\` needs more than 0 and less than 180 degrees, not ${String(stated)}`;
  }

  /*
   * `trapezoid` carries a length instead: the far parallel side.
   *
   * Read the same way and for the same reason. Equal to `depth` it would close a parallelogram,
   * which is what the shape is without this clause, so it is refused rather than silently drawing
   * the figure the writer was trying to avoid.
   */
  const baseFar = sloped === -1 ? undefined : Number(tokens[sloped + 2]?.value);
  if (sloped !== -1 && (tokens[sloped + 2]?.kind !== 'number' || !Number.isFinite(baseFar))) {
    return `\`${word}\` takes a corner and the length of the far parallel side: \`${example}\``;
  }
  if (baseFar !== undefined && baseFar <= 0) {
    return `\`${word}\` needs a positive far side, not ${String(baseFar)}`;
  }

  // Every clause closes the line, so the size numbers stop at whichever one starts first.
  const clause = [over, clauseAt].filter((found) => found > size).sort((a, b) => a - b)[0];

  /*
   * With `base` the size numbers are one, not three: the ring says how wide and how deep the
   * solid is, and `size` is left with only the height. Repeating a width beside a list of corners
   * would be two answers to one question, and the parser would have to pick.
   */
  const wanted = (listed === -1 ? 3 : 1) + (anchor.name === null ? 2 : 0);
  const numbers = [
    ...tokens.slice(at + 1 + (anchor.name === null ? 0 : 1), size),
    ...tokens.slice(size + 1, clause),
  ];
  if (numbers.length !== wanted || numbers.some((token) => token.kind !== 'number')) {
    return listed === -1
      ? `${keyword} takes \`at x y\` and \`size width height depth\`: ${shape}`
      : `with \`base\`, \`size\` is the height alone: \`${keyword} … at 0 0 size 240 base 0 0 200 0 260 120\``;
  }

  /*
   * The ring, checked and turned the right way round by the geometry that will build from it —
   * one place deciding what a base is, rather than a parser opinion beside a builder opinion.
   */
  const corners =
    listed === -1
      ? null
      : orientedBase(
          tokens.slice(listed + 1).map((token) => Number(token.value)),
          after.length
        );
  if (typeof corners === 'string') return corners;
  if (corners !== null && tokens.slice(listed + 1).some((token) => token.kind !== 'number')) {
    return '`base` takes plain numbers: `base 0 0 200 0 260 120`';
  }

  const span = (pick: (point: { x: number; z: number }) => number): number => {
    const values = (corners ?? []).map(pick);
    return Math.max(...values) - Math.min(...values);
  };

  /*
   * With `at I` the coordinates are absent and `numbers` begins at the size. Defaulted to the
   * origin rather than left undefined: the anchor entity is what the solid is actually placed
   * by, and `spec.at` is then only the fallback nobody reads.
   */
  const values = numbers.map((token) => Number(token.value));
  const [x, y, ...rest] = anchor.name === null ? values : [0, 0, ...values];

  /*
   * With a listed ring the width and the depth are *read back off it* rather than stated, so
   * everything downstream — the flatness guard, the placement, the file — sees the same two
   * numbers it would have seen for any other base. Nothing has to know which way the shape
   * arrived.
   */
  const width = corners === null ? rest[0] : span((point) => point.x);
  const height = corners === null ? rest[1] : rest[0];
  const depth = corners === null ? rest[2] : span((point) => point.z);
  if (
    x === undefined ||
    y === undefined ||
    width === undefined ||
    height === undefined ||
    depth === undefined
  ) {
    return `${keyword} takes \`at x y\` and \`size width height depth\`: ${shape}`;
  }
  if (width <= 0 || height <= 0 || depth <= 0) {
    // A zero extent collapses the solid into a flat figure whose faces have no normal, so
    // every edge classifies as hidden and the whole thing draws dashed. Refusing is kinder.
    return `${keyword} needs a positive width, height and depth`;
  }

  /*
   * A solid too small to carry its own labels.
   *
   * These numbers are *drawing* units and the document has no scale, so nothing about a tiny
   * solid is wrong — until it is exported. An export frames the content and then writes the
   * names at a fixed size, so a room given as `size 4 3.2 4` because the problem says four
   * metres comes out as a 106-pixel picture with the letters larger than the room. That happened
   * on the first problem that stated real lengths.
   *
   * Judged on the largest dimension, because a solid that is thin in one direction is the
   * flatness rule's business and this one is about the figure as a whole.
   */
  const tooSmall = illegible(width, height, depth);
  if (tooSmall !== null) return tooSmall;

  /*
   * A base so much longer than it is deep that it reads as a line rather than a face.
   *
   * Note what this is *not* checking. A base twice as wide as it is deep is a two-to-one
   * rectangle and drawing it as a thin parallelogram is correct — the shear angle is fixed, so
   * the base is never near an edge-on view and no ratio of `width` to `depth` is a projection
   * defect. What a ratio can do is make the face vanishingly thin on the page, and past about
   * four to one there is not enough of it left for a reader to see a plane at all.
   *
   * Refused rather than warned, because there is no warning channel and a silent sliver is the
   * failure this exists to prevent.
   */
  const placement = { at: vec.vec2(x, y), width, height, depth };
  const flatRing = corners === null ? undefined : corners.flatMap((point) => [point.x, point.z]);
  const all = [...left, ...right];
  if (new Set(all).size !== all.length) return `${keyword} names a corner twice`;

  const named = listed === -1 ? (words[clauseAt + 1] ?? '') : '';
  const found =
    listed !== -1
      ? -1
      : keyword === 'pyramid'
        ? right.indexOf(named)
        : Math.max(left.indexOf(named), right.indexOf(named));
  if (clauseAt !== -1 && listed === -1 && found === -1) {
    return `\`${word} ${named}\` is not a corner of the base`;
  }
  const baseAngleAt = clauseAt === -1 || listed !== -1 ? undefined : found;

  /*
   * Where the far side starts. Flush against the leg for `trapezoid`, which with the right angle
   * is "hình thang vuông tại A và D"; centred for `isosceles`, which puts the two legs at mirror
   * images of each other and is the only way to get "hình thang cân" — a flush trapezium has
   * legs of different lengths for every far side but one, and that one is the parallelogram.
   */
  const baseFarOffset = even === -1 || baseFar === undefined ? undefined : (depth - baseFar) / 2;

  /*
   * A trapezium whose two parallels are equal is a parallelogram, and the word says otherwise.
   *
   * Refused rather than drawn, because it is a mistake with no symptom: the figure is a perfectly
   * good parallelogram under a caption reading "hình thang", and every check passes. A model
   * given "AB = a, CD = 2a" wrote `size 200 … 400 isosceles A 400` within an hour of the clause
   * existing — the near parallel is `depth` and the far one is the clause's own number, and it
   * had put the same length in both.
   */
  if (baseFar !== undefined && Math.abs(baseFar - depth) < 1e-9) {
    return (
      `\`${word} ${named} ${String(baseFar)}\` makes both parallel sides ${String(depth)}, ` +
      'which is a parallelogram — `depth` is the near side and this is the far one. ' +
      'Say `angle` if a parallelogram is what you meant'
    );
  }

  /*
   * A base so much longer than it is deep that it reads as a line rather than a face.
   *
   * Note what this is *not* checking. A base twice as wide as it is deep is a two-to-one
   * rectangle and drawing it as a thin parallelogram is correct — the shear angle is fixed, so
   * the base is never near an edge-on view and no ratio is a projection defect. What a ratio can
   * do is make the face vanishingly thin on the page, and past about four to one there is not
   * enough of it left for a reader to see a plane at all.
   *
   * Measured from the base the solid will really be built on rather than from the two `size`
   * numbers, because with an angle those are the *sides* and not the extents: a rhombus of side
   * 210 with a 5° corner is a sliver 419 long and 18 deep, and the numbers say 210 by 210.
   *
   * Refused rather than warned, because there is no warning channel and a silent sliver is the
   * failure this exists to prevent.
   */
  /*
   * A base with no shape clause is *regular*, and a regular polygon has one measure across.
   *
   * Two different numbers describe an ellipse, and the corners inscribed in one are not a regular
   * polygon at all: `size 300 … 200` on six corners gives sides from 100 to 139, under a caption
   * that says "lục giác đều". The shape is wrong in space, not merely foreshortened on the page,
   * so no amount of looking at the figure tells a reader which of the two to believe.
   *
   * Four corners are exempt because there the two numbers mean a rectangle, which is a shape and
   * a common one. Everywhere else they are refused rather than one of them quietly ignored — the
   * writer said something about the base, and dropping half of it is how the figure and the words
   * come apart in the first place.
   */
  const ring = keyword === 'pyramid' ? right.length : left.length;
  if (clauseAt === -1 && ring !== 4 && Math.abs(width - depth) > 1e-9) {
    const shapeOf = ring === 3 ? 'an equilateral triangle' : `a regular ${String(ring)}-gon`;
    return (
      `${shapeOf} has one measure across, so width and depth must match: ` +
      `\`size ${String(width)} … ${String(width)}\`. For another shape say which — ` +
      '`right B`, `angle A 60`, `trapezoid A 420` or `isosceles A 420`'
    );
  }

  const flattest = 4;
  const extent = baseExtent(
    keyword === 'pyramid' ? right.length : left.length,
    placement,
    baseAngleAt === undefined
      ? undefined
      : {
          at: baseAngleAt,
          degrees: stated,
          ...(baseFar === undefined ? {} : { far: baseFar }),
          ...(baseFarOffset === undefined ? {} : { offset: baseFarOffset }),
        },
    corners ?? undefined
  );
  if (extent.depth * flattest < extent.width || extent.width * flattest < extent.depth) {
    const ratio = Math.max(extent.width / extent.depth, extent.depth / extent.width);
    return `the base is ${ratio.toFixed(1)} times longer than it is deep and will read as a line, not a face`;
  }

  /*
   * The foot, named by the base corners it sits on: none for the centre, one for a corner
   * (`SA ⊥ (ABCD)`), two for the midpoint of that edge (`(SAB) ⊥ (ABCD)`).
   *
   * Read once for both keywords, because it means the same thing in both — *the thing that goes
   * up is directly above the average of these* — and a rule stated twice is a rule that ends up
   * meaning two things. What differs is only which ring the names are looked up in: a pyramid
   * writes its apex before the `.` and its base after, and a prism the other way round.
   */
  const baseNames = keyword === 'pyramid' ? right : left;
  const feet =
    over === -1
      ? []
      : tokens
          .slice(over + 1, clauseAt > over ? clauseAt : undefined)
          .map((token) => String(token.value));
  if (feet.length > 2) {
    return '`over` takes a base corner, or two for the midpoint of the edge between them';
  }
  const footIndices = feet.map((name) => baseNames.indexOf(name));
  const absent = feet.filter((_, k) => footIndices[k] === -1);
  if (absent.length > 0) {
    return `\`over ${absent.join(' ')}\` is not a corner of the base`;
  }

  if (keyword === 'pyramid') {
    const [apex] = left;
    if (left.length !== 1 || apex === undefined) {
      return `a pyramid has one apex before the \`.\`: ${shape}`;
    }
    if (right.length < 3) return 'a pyramid needs at least three base corners';

    // The apex comes last, because that is where the solid's own numbering puts it.
    return {
      solid: 'pyramid',
      at: placement.at,
      ...centred,
      width,
      height,
      depth,
      corners: right.length,
      foot: footIndices,
      ...(baseAngleAt === undefined
        ? {}
        : {
            baseAngleAt,
            baseAngle: stated,
            ...(baseFar === undefined ? {} : { baseFar }),
            ...(baseFarOffset === undefined ? {} : { baseFarOffset }),
          }),
      ...(flatRing === undefined ? {} : { baseCorners: flatRing }),
      order: [...right, apex],
      rings: new Map(),
    };
  }

  if (left.length !== right.length) {
    return `a prism needs the same number of corners on each side of the \`.\``;
  }
  if (left.length < 3) return 'a prism needs at least three corners in each ring';
  return {
    solid: 'prism',
    at: placement.at,
    ...centred,
    width,
    height,
    depth,
    corners: left.length,
    foot: footIndices,
    ...(baseAngleAt === undefined
      ? {}
      : {
          baseAngleAt,
          baseAngle: stated,
          ...(baseFar === undefined ? {} : { baseFar }),
          ...(baseFarOffset === undefined ? {} : { baseFarOffset }),
        }),
    ...(flatRing === undefined ? {} : { baseCorners: flatRing }),
    order: [...left, ...right],
    rings: new Map(),
  };
}

/** The solids whose base is a circle, so `size` is a diameter and a height. */
const ROUND = new Set(['cone', 'cylinder']);

/**
 * The sphere, which is round but takes **one** number.
 *
 * Not in {@link ROUND} because that set means "a diameter and a height", and a sphere has only
 * the one measurement — `size 300 300` would be two ways of saying the same thing, and the day
 * they disagreed the script would be asking for an ellipsoid. No problem in the book wants one.
 */
const BALL = 'sphere';

/**
 * Every statement that builds a solid.
 *
 * Exported so the reference can be checked against it. These are handled here rather than in
 * `STATEMENTS`, which means the generated grammar cannot reach them — and all four went
 * undocumented for exactly that reason, while the reference went on describing the way solids
 * were drawn before they existed. A model handed that guessed the syntax and got it wrong every
 * time. Binding the two together is what stops the next one being added the same way.
 */
export const SOLID_KEYWORDS: ReadonlySet<string> = new Set([...ROUND, BALL, 'prism', 'pyramid']);

/** Statements that need more than their declared parameters to mean anything. */
function minimumFor(keyword: string): number {
  return keyword === 'polygon' ? 3 : 2;
}

/* -------------------------------------------------------------------- scanning */

type Token =
  | { readonly kind: 'word'; readonly value: string }
  | { readonly kind: 'number'; readonly value: number }
  | { readonly kind: 'string'; readonly value: string };

/**
 * Removes a trailing comment.
 *
 * A `#` inside quotes is part of the text, not a comment — a label reading `#1` is entirely
 * reasonable and losing half of it would be a silent corruption.
 */
function stripComment(line: string): string {
  let inString = false;
  for (let index = 0; index < line.length; index++) {
    const character = line[index];
    if (character === '"') inString = !inString;
    else if (character === '#' && !inString) return line.slice(0, index);
  }
  return line;
}

/**
 * Splits `name = rest`, or reports the whole line as a bare statement.
 *
 * Only an `=` outside quotes counts, and only when what precedes it is a single word — so
 * `label A "x = y"` is a statement about a label, not an assignment.
 */
function splitAssignment(line: string): { name: string | null; rest: string } | null {
  let inString = false;
  for (let index = 0; index < line.length; index++) {
    const character = line[index];
    if (character === '"') inString = !inString;
    if (character !== '=' || inString) continue;

    const left = line.slice(0, index).trim();
    const right = line.slice(index + 1).trim();
    /*
     * A prime counts as part of the name, because every Vietnamese textbook writes the second
     * ring of a prism as A'B'C'D'. The tokeniser has always allowed it — it splits on spaces —
     * so `midpoint C C'` worked while `BD' = segment B D'` did not, which is a seam with no
     * reason behind it: the same word was a name in one position and a syntax error in the
     * other. Both spellings are accepted, the typed apostrophe and the character a word
     * processor substitutes for it.
     */
    if (!/^[A-Za-z_][A-Za-z0-9_'′]*$/.test(left)) break;
    if (right === '') return null;
    return { name: left, rest: right };
  }
  return { name: null, rest: line };
}

function tokenise(text: string): readonly Token[] {
  const tokens: Token[] = [];
  let index = 0;

  while (index < text.length) {
    const character = text[index];
    if (character === undefined) break;

    if (character === ' ' || character === '\t' || character === ',') {
      index += 1;
      continue;
    }

    if (character === '"') {
      const end = text.indexOf('"', index + 1);
      // An unterminated quote takes the rest of the line rather than losing the text.
      const close = end === -1 ? text.length : end;
      tokens.push({ kind: 'string', value: text.slice(index + 1, close) });
      index = close + 1;
      continue;
    }

    let end = index;
    while (end < text.length && !' \t,'.includes(text[end] ?? '')) end += 1;
    const word = text.slice(index, end);
    index = end;

    const asNumber = Number(word);
    if (word !== '' && Number.isFinite(asNumber) && /^[-+]?[0-9.]/.test(word)) {
      tokens.push({ kind: 'number', value: asNumber });
    } else {
      tokens.push({ kind: 'word', value: word });
    }
  }

  return tokens;
}
